import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase, uploadDesignerAvatar, getImagePreview, uploadImage } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import CardNav from './CardNav';
import ProfileCard from './ProfileCard';
import VoiceWaveform from './VoiceWaveform';
import Leaderboard from './Leaderboard';
import ResourceSection from './ResourceSection';
import { generateSpeechUrl, generateSpeech, isConfigured } from '../lib/elevenlabsService';
import NeonCountdown from './NeonCountdown';
import '../App.css';

function ActiveGameView({ game, onBack, onNavigateToReview }) {
  const { isHost, user, userProfile } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [currentSessionIndex, setCurrentSessionIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null); // in seconds
  const [timerStarted, setTimerStarted] = useState(false);
  const [allSessionsComplete, setAllSessionsComplete] = useState(false);
  const [completedSessions, setCompletedSessions] = useState(new Set()); // Track which sessions are complete
  const [newDesignerName, setNewDesignerName] = useState('');
  const [addingDesigner, setAddingDesigner] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [participantUserIds, setParticipantUserIds] = useState(new Set()); // Track which user IDs have joined
  const [knownParticipantCount, setKnownParticipantCount] = useState(0); // Track total participant count for polling
  const [activePlayerIds, setActivePlayerIds] = useState(new Set()); // Track which players are currently online (presence)
  const [activePlayerEmails, setActivePlayerEmails] = useState(new Set()); // Track email-only players who are online
  // Local game status to track real-time updates
  const [localGameStatus, setLocalGameStatus] = useState(game?.game_status || 'draft');
  const [joinCode, setJoinCode] = useState(game?.join_code || null);
  const [isStartingGame, setIsStartingGame] = useState(false); // Flag to prevent status check from triggering reload
  const [isExiting, setIsExiting] = useState(false); // Flag to prevent re-rendering after exit is confirmed
  const isExitingRef = useRef(false); // Ref version for use in callbacks/closures
  const [stableWaitingScreen, setStableWaitingScreen] = useState(true); // Debounced waiting screen state
  const waitingScreenTimeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const hasStartedIntervalRef = useRef(false);
  
  // Voice reading state
  const [clientChanges, setClientChanges] = useState([]);
  const [bonusChange, setBonusChange] = useState(null);
  const [voiceTimingConfig, setVoiceTimingConfig] = useState(null);
  const [readChanges, setReadChanges] = useState(new Set()); // Track which changes have been read
  const [isVoicePlaying, setIsVoicePlaying] = useState(false);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [generatingVoiceForIndex, setGeneratingVoiceForIndex] = useState(null); // Track which change is generating voice (-1 for bonus)
  const voiceCheckIntervalRef = useRef(null);
  const isPlayingAudioRef = useRef(false); // Ref to prevent multiple simultaneous calls
  const voiceTimeoutRef = useRef(null); // Ref to store timeout ID for clearing
  
  // Design submission state
  const [submissions, setSubmissions] = useState([]);
  const [selectedDesignerForUpload, setSelectedDesignerForUpload] = useState(null);
  const [isUploadingDesign, setIsUploadingDesign] = useState(false);
  const fileInputRef = useRef(null);
  const [sessionRequirements, setSessionRequirements] = useState([]);
  
  // Designer upload state (for designer view)
  const [isDragging, setIsDragging] = useState(false);
  const [designerUploadFile, setDesignerUploadFile] = useState(null);
  const [isUploadingDesignerSubmission, setIsUploadingDesignerSubmission] = useState(false);
  const designerFileInputRef = useRef(null);
  
  // Resources state
  const [resources, setResources] = useState([]);
  
  // Use refs to track current values for the interval callback
  const voiceTimingConfigRef = useRef(null);
  const clientChangesRef = useRef([]);
  const bonusChangeRef = useRef(null);
  const readChangesRef = useRef(new Set());
  const sessionsRef = useRef([]);
  const currentSessionIndexRef = useRef(0);
  const timeRemainingRef = useRef(null);
  const localGameStatusRef = useRef(game?.game_status || 'draft'); // Ref for status check interval
  
  // Voice queue and deduplication
  const voiceQueueRef = useRef([]); // Queue of voice clips to play
  const playedVoiceIdsRef = useRef(new Set()); // Track which voice clips have been played (for deduplication)
  
  // Check ElevenLabs API key on mount
  useEffect(() => {
    const apiKeyConfigured = isConfigured();
    console.log('[VOICE-INIT] ElevenLabs API key check:', {
      configured: apiKeyConfigured,
      envVar: process.env.REACT_APP_ELEVENLABS_API_KEY ? 'SET (hidden)' : 'NOT SET',
      timestamp: new Date().toISOString()
    });
    if (!apiKeyConfigured) {
      console.warn('[VOICE-INIT] ⚠️ ElevenLabs API key not configured! Voice features will not work.');
      console.warn('[VOICE-INIT] Set REACT_APP_ELEVENLABS_API_KEY in your .env file');
    }
  }, []);

  // Suppress GSAP errors (they're non-critical and clutter the console)
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      // Suppress GSAP "target not found" errors
      const errorString = args.join(' ');
      if (errorString.includes('GSAP') && (errorString.includes('target not found') || errorString.includes('gasp.com'))) {
        // Silently ignore GSAP target errors
        return;
      }
      // Pass through all other errors
      originalError.apply(console, args);
    };
    
    return () => {
      console.error = originalError;
    };
  }, []);

  // Keep refs in sync with state
  useEffect(() => {
    voiceTimingConfigRef.current = voiceTimingConfig;
  }, [voiceTimingConfig]);
  
  useEffect(() => {
    clientChangesRef.current = clientChanges;
  }, [clientChanges]);
  
  useEffect(() => {
    bonusChangeRef.current = bonusChange;
  }, [bonusChange]);
  
  useEffect(() => {
    readChangesRef.current = readChanges;
  }, [readChanges]);
  
  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);
  
  useEffect(() => {
    currentSessionIndexRef.current = currentSessionIndex;
  }, [currentSessionIndex]);
  
  useEffect(() => {
    timeRemainingRef.current = timeRemaining;
  }, [timeRemaining]);
  
  useEffect(() => {
    localGameStatusRef.current = localGameStatus;
  }, [localGameStatus]);

  // Fetch join code if missing and game is published
  useEffect(() => {
    const fetchJoinCode = async () => {
      if (game?.id && !joinCode && (localGameStatus === 'published' || localGameStatus === 'started')) {
        console.log('[ActiveGameView] Fetching join code...');
        const { data, error } = await supabase
          .from('games')
          .select('join_code')
          .eq('id', game.id)
          .single();
          
        if (data && data.join_code) {
          console.log('[ActiveGameView] Fetched join code:', data.join_code);
          setJoinCode(data.join_code);
        } else if (error) {
          console.error('[ActiveGameView] Error fetching join code:', error);
        }
      }
    };
    fetchJoinCode();
  }, [game?.id, joinCode, localGameStatus]);

  const handleExit = async () => {
    // If game is in draft mode OR local status is draft, just navigate away without warning
    // Check localGameStatus first (most up-to-date), then fallback to game prop
    if (localGameStatus === 'draft' || game?.game_status === 'draft') {
      console.log('🔄 Game is already in draft, navigating away immediately');
      if (onBack) {
        onBack();
      }
      return;
    }

    // For players, just navigate away without warning (they can't affect game status)
    if (!isHost()) {
      if (onBack) {
        onBack();
      }
      return;
    }

    // For hosts with started games, show warning and revert to draft
    // BUT: Don't allow reverting if timer has started (prevent database inconsistency)
    if (game?.timer_started_at) {
      const confirmed = window.confirm(
        'The timer has already started. Exiting will cancel the game, but the timer will remain in the database. Are you sure you want to exit?'
      );
      if (!confirmed) return;
      // Just navigate away, don't revert status if timer started
      if (onBack) {
        onBack();
      }
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to exit? This will cancel the game progress and revert it back to draft mode. All timer progress will be lost.'
    );

    if (confirmed) {
      // Set exiting flag immediately to prevent any re-renders or real-time updates
      setIsExiting(true);
      isExitingRef.current = true; // Also set ref for use in callbacks
      console.log('[HOST] 🚪 Exit confirmed - setting isExiting flag to prevent re-renders');
      
      try {
        console.log('🔄 Attempting to revert game to draft:', game.id);
        console.log('[STATUS-UPDATE] Changing status from', game?.game_status, 'to draft at', new Date().toISOString());
        console.log('🔄 isHost() check:', isHost());
        console.log('🔄 Current game status:', game?.game_status);
        console.log('🔄 User ID:', user?.id);
        console.log('🔄 Game created_by:', game?.created_by);
        
        // Delete old voice clips when game resets to draft (allows re-playing on restart)
        console.log('🗑️ Cleaning up voice clips for game reset...');
        const { error: deleteClipsError } = await supabase
          .from('voice_clips')
          .delete()
          .eq('game_id', game.id);
        
        if (deleteClipsError) {
          console.error('⚠️ Error deleting voice clips:', deleteClipsError);
          // Don't fail the entire operation if clip deletion fails
        } else {
          console.log('✅ Voice clips deleted successfully - game can re-play client changes on restart');
        }
        
        // Revert game status to draft and clear timer fields
        // Only allow this if timer hasn't started
        const { data, error } = await supabase
          .from('games')
          .update({ 
            game_status: 'draft',
            timer_started_at: null,
            current_session_index: 0,
            timer_duration_seconds: null
          })
          .eq('id', game.id)
          .select();

        if (error) {
          console.error('❌ Error reverting game to draft:', error);
          console.error('Error details:', {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint
          });
          alert(`Failed to exit game: ${error.message || 'Please try again.'}`);
          return;
        }

        if (data && data.length > 0) {
          console.log('✅ Successfully reverted game to draft:', data[0]);
          console.log('Updated game status:', data[0].game_status);
          
          // CRITICAL: Update local state immediately to prevent re-showing confirm dialog
          setLocalGameStatus('draft');
        } else {
          console.warn('⚠️ Update succeeded but no data returned');
          // Still update local state even if no data returned
          setLocalGameStatus('draft');
        }

        // Clear timer from localStorage
        sessions.forEach(session => {
          const timerKey = `game_timer_${game.id}_${session.sessionKey}`;
          localStorage.removeItem(timerKey);
        });

        // CRITICAL: Navigate away immediately after successful update
        // Call onBack synchronously to ensure navigation happens before any re-renders
        console.log('[HOST] 🔄 Navigating away after game revert to draft');
        console.log('[HOST] isExiting flag:', true, '- this should prevent re-renders');
        console.log('[HOST] onBack callback available:', !!onBack);
        console.log('[HOST] isHost check:', isHost());
        
        // Force navigation - try multiple methods
        let navigationAttempted = false;
        
        if (onBack) {
          try {
            console.log('[HOST] Calling onBack() callback to navigate away...');
            onBack();
            navigationAttempted = true;
            console.log('[HOST] ✅ onBack() called successfully');
            
            // Give React a moment to process the state update, then force navigation if needed
            setTimeout(() => {
              if (isExitingRef.current) {
                console.log('[HOST] ⚠️ Still exiting after 500ms - forcing navigation via window.location');
                window.location.href = '/';
              }
            }, 500);
          } catch (error) {
            console.error('[HOST] ❌ Error calling onBack():', error);
            // Fallback: force navigation via window.location
            console.log('[HOST] Using fallback navigation due to error...');
            window.location.href = '/';
            navigationAttempted = true;
          }
        } else {
          console.error('[HOST] ❌ onBack callback is not available! Using fallback...');
          window.location.href = '/';
          navigationAttempted = true;
        }
        
        if (!navigationAttempted) {
          console.error('[HOST] ❌ No navigation method executed! Forcing fallback...');
          window.location.href = '/';
        }
        
        // Don't reset isExiting flag - let component unmount naturally
        return; // Exit early to prevent any further execution
      } catch (error) {
        console.error('[HOST] ❌ Exception while exiting game:', error);
        setIsExiting(false); // Reset flag on error so user can try again
        isExitingRef.current = false; // Also reset ref
        alert(`Failed to exit game: ${error.message || 'Please try again.'}`);
      }
    }
  };

  // Check if user has access to this game (host, authenticated participant, or email-only participant)
  useEffect(() => {
    const checkAccess = async () => {
      if (!game?.id) {
        setHasAccess(false);
        setCheckingAccess(false);
        return;
      }

      // If user is a host, they have access
      if (isHost()) {
        setHasAccess(true);
        setCheckingAccess(false);
        return;
      }

      // If user is authenticated, check by user_id
      if (user?.id) {
      try {
        const { data, error } = await supabase
          .from('game_participants')
          .select('id')
          .eq('game_id', game.id)
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
          console.error('Error checking game access:', error);
          setHasAccess(false);
        } else {
          setHasAccess(!!data);
        }
      } catch (error) {
        console.error('Error checking game access:', error);
        setHasAccess(false);
      } finally {
        setCheckingAccess(false);
      }
        return;
      }

      // If user is NOT authenticated, check if game allows public access (published/started)
      // For email-only joins, we allow access if game is published/started
      // The actual participant check happens when they try to submit
      if (game.game_status === 'published' || game.game_status === 'started') {
        setHasAccess(true);
        setCheckingAccess(false);
        return;
      }

      // Game is draft or not accessible
      setHasAccess(false);
      setCheckingAccess(false);
    };

    checkAccess();
  }, [game?.id, game?.game_status, user?.id, isHost]);

  useEffect(() => {
    if (game && hasAccess) {
      loadGameData();
      loadVoiceTimingConfig();
      loadResources();
      // Also reload game status from database to ensure we have the latest status
      loadGameStatus();
      // Check for existing timer in database
      checkAndSyncTimer();
    }
  }, [game, hasAccess]);

  // Check if game is already finished when component loads
  useEffect(() => {
    if (game?.game_status === 'finished' || localGameStatus === 'finished') {
      console.log('[INIT] Game is already finished - setting allSessionsComplete to true');
      setAllSessionsComplete(true);
    }
  }, [game?.game_status, localGameStatus]);

  // Reload game data when status changes to published (to ensure sessions are loaded)
  useEffect(() => {
    if (game?.id && hasAccess && (localGameStatus === 'published' || game?.game_status === 'published')) {
      console.log('[STATUS-CHANGE] Game published, ensuring sessions are loaded...');
      if (sessions.length === 0) {
        console.log('[STATUS-CHANGE] Sessions empty, reloading game data...');
        loadGameData();
      } else if (sessions.length > 0 && clientChanges.length === 0 && currentSessionIndex < sessions.length) {
        // Sessions are loaded but clientChanges aren't - load them
        console.log('[STATUS-CHANGE] Sessions loaded but clientChanges empty, loading session changes...');
        loadSessionChanges();
      }
    }
  }, [localGameStatus, game?.game_status, game?.id, hasAccess, sessions.length, clientChanges.length, currentSessionIndex]);

  // Log game object updates for debugging
  useEffect(() => {
    console.log('[GAME-UPDATE] Game object updated:', {
      game_status: game?.game_status,
      timer_started_at: game?.timer_started_at,
      timer_duration_seconds: game?.timer_duration_seconds,
      current_session_index: game?.current_session_index,
      isHost: isHost(),
      localGameStatus,
      timerStarted
    });
  }, [game?.game_status, game?.timer_started_at, game?.timer_duration_seconds, game?.current_session_index]);

  // Auto-navigate away if game becomes draft (for hosts) - but only if we're not already exiting
  useEffect(() => {
    if (isHost() && (localGameStatus === 'draft' || game?.game_status === 'draft') && !isExitingRef.current) {
      console.log('[AUTO-NAV] Game is in draft mode, auto-navigating away...', {
        localGameStatus,
        gameStatus: game?.game_status,
        isExiting: isExitingRef.current
      });
      setIsExiting(true);
      isExitingRef.current = true;
      if (onBack) {
        console.log('[AUTO-NAV] Calling onBack() to navigate...');
        onBack();
      } else {
        console.error('[AUTO-NAV] onBack not available, using window.location fallback');
        window.location.href = '/';
      }
    }
  }, [localGameStatus, game?.game_status, isHost, onBack]);

  // Sync local game status with prop when it changes (but skip if user is starting the game)
  useEffect(() => {
    if (game?.game_status && !isStartingGame) {
      // If timer has started, ignore any status changes back to draft/published (invalid state)
      if (game.timer_started_at && (game.game_status === 'draft' || game.game_status === 'published')) {
        console.log('[STATUS-SYNC] Ignoring invalid status change - timer already started. Status:', game.game_status);
        console.trace('[STATUS-SYNC] Call stack:');
        return;
      }
      
      // Don't allow reverting from 'started' to earlier states
      if (localGameStatus === 'started' && (game.game_status === 'draft' || game.game_status === 'published')) {
        console.log('[STATUS-SYNC] Blocked: Cannot revert from "started" to "' + game.game_status + '"');
        console.trace('[STATUS-SYNC] Call stack:');
        return;
      }
      
      // CRITICAL FIX: Don't allow reverting from 'published' to 'draft' once published
      // This prevents flickering when polling/real-time updates cause status to bounce
      if (localGameStatus === 'published' && game.game_status === 'draft') {
        console.log('[STATUS-SYNC] ⚠️ Blocked: Cannot revert from "published" to "draft" (prevents flicker)');
        return;
      }
      
      // Only sync if status actually changed AND it's a valid forward transition
      if (game.game_status !== localGameStatus) {
        // Valid transitions: draft → published → started → finished
        const validTransitions = {
          'draft': ['published'],
          'published': ['started'],
          'started': ['finished'],
          'finished': [] // No transitions from finished
        };
        
        const allowedNextStatuses = validTransitions[localGameStatus] || [];
        if (allowedNextStatuses.includes(game.game_status)) {
          console.log('[STATUS-SYNC] ✅ Valid transition: Syncing local status from prop:', localGameStatus, '→', game.game_status);
          console.log('[STATUS-UPDATE] Changing status from', localGameStatus, 'to', game.game_status, 'at', new Date().toISOString());
          setLocalGameStatus(game.game_status);
        } else {
          console.log('[STATUS-SYNC] ⚠️ Blocked invalid transition:', localGameStatus, '→', game.game_status, '(prevents flicker)');
        }
      }
    }
  }, [game?.game_status, game?.timer_started_at, isStartingGame, localGameStatus]);

  // Debounce waiting screen to prevent flickering
  useEffect(() => {
    // Show waiting screen ONLY when:
    // - Not host
    // - Game is still in 'draft' status (not published yet)
    // - Timer has NOT started
    // Once game is 'published', players should see game details, not waiting screen
    const shouldShowWaitingScreen = !isHost() && 
                                    localGameStatus === 'draft' && 
                                    !game?.timer_started_at;
    
    console.log('[WAITING-SCREEN] Checking visibility:', {
      isHost: isHost(),
      localGameStatus,
      timer_started_at: game?.timer_started_at,
      shouldShowWaitingScreen
    });
    
    // Clear any existing timeout
    if (waitingScreenTimeoutRef.current) {
      clearTimeout(waitingScreenTimeoutRef.current);
    }
    
    // If we should show waiting screen, update immediately
    if (shouldShowWaitingScreen) {
      console.log('[WAITING-SCREEN] ✅ Showing waiting screen');
      setStableWaitingScreen(true);
    } else {
      // If we should hide it, wait 500ms to ensure it's stable
      console.log('[WAITING-SCREEN] ⏳ Hiding waiting screen (will hide in 500ms)');
      waitingScreenTimeoutRef.current = setTimeout(() => {
        console.log('[WAITING-SCREEN] ✅ Hiding waiting screen now');
        setStableWaitingScreen(false);
      }, 500);
    }
    
    return () => {
      if (waitingScreenTimeoutRef.current) {
        clearTimeout(waitingScreenTimeoutRef.current);
      }
    };
  }, [localGameStatus, game?.timer_started_at, isHost]);

  // NOTE: Removed clock sync approach - it was causing issues with stale updated_at timestamps
  // Instead, we calculate elapsed time directly from timer_started_at using client clock
  // Both clients will be synchronized within ~100-200ms, which is acceptable for game timers

  // Periodically check game status to detect changes (but skip if user is starting the game)
  useEffect(() => {
    if (!game?.id || !hasAccess || isStartingGame) {
      console.log('[STATUS-CHECK] Skipping status check:', { hasGame: !!game?.id, hasAccess, isStartingGame });
      return;
    }
    
    const statusCheckInterval = setInterval(async () => {
      // Skip check if user is currently starting the game
      if (isStartingGame) {
        console.log('[STATUS-CHECK] Skipping - user is starting game');
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('games')
          .select('game_status, timer_started_at')
          .eq('id', game.id)
          .single();
        
        const currentStatus = localGameStatusRef.current;
        if (!error && data && data.game_status !== currentStatus) {
          // If timer has started, ignore any status changes back to draft/published (invalid state)
          if (data.timer_started_at && (data.game_status === 'draft' || data.game_status === 'published')) {
            console.log('[STATUS-CHECK] Ignoring invalid status change - timer already started. Status:', data.game_status);
            console.log('[STATUS-CHECK] Current status:', currentStatus, 'New status:', data.game_status);
            return;
          }
          
          // Don't allow reverting from 'started' to earlier states
          if (currentStatus === 'started' && (data.game_status === 'draft' || data.game_status === 'published')) {
            console.log('[STATUS-CHECK] Blocked: Cannot revert from "started" to "' + data.game_status + '"');
            console.log('[STATUS-CHECK] This is likely a database inconsistency - ignoring invalid status change');
            return;
          }
          
          console.log('[STATUS-CHECK] 🔄 External status change detected:', currentStatus, '→', data.game_status);
          console.log('[STATUS-CHECK] This is an external change (not from user action)');
          console.log('[STATUS-UPDATE] Changing status from', currentStatus, 'to', data.game_status, 'at', new Date().toISOString());
          setLocalGameStatus(data.game_status);
          
          // If game status changed to 'finished', set allSessionsComplete to true
          if (data.game_status === 'finished' && currentStatus !== 'finished') {
            console.log('[STATUS-CHECK] ✅ Game finished - setting allSessionsComplete to true');
            setAllSessionsComplete(true);
          }
          
          // If game was reverted to draft, reset state (only if timer hasn't started)
          if (data.game_status === 'draft' && (currentStatus === 'started' || currentStatus === 'published') && !data.timer_started_at) {
            console.log('[STATUS-CHECK] 🔄 Game reverted to draft - resetting state');
            setTimerStarted(false);
            setTimeRemaining(null);
            setCompletedSessions(new Set());
            setAllSessionsComplete(false);
            setCurrentSessionIndex(0);
            setIsVoicePlaying(false);
            if (currentAudioUrl) {
              URL.revokeObjectURL(currentAudioUrl);
              setCurrentAudioUrl(null);
            }
            // Clear voice timeout if exists
            if (voiceTimeoutRef.current) {
              clearTimeout(voiceTimeoutRef.current);
              voiceTimeoutRef.current = null;
            }
            isPlayingAudioRef.current = false;
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
              hasStartedIntervalRef.current = false;
            }
            if (voiceCheckIntervalRef.current) {
              clearInterval(voiceCheckIntervalRef.current);
              voiceCheckIntervalRef.current = null;
            }
          }
          // NOTE: We do NOT navigate/reload when status changes from 'published' to 'started'
          // The status change is just a state update, not a reason to reload
        }
      } catch (error) {
        console.error('[STATUS-CHECK] Error checking game status:', error);
      }
    }, 2000); // Check every 2 seconds
    
    return () => clearInterval(statusCheckInterval);
  }, [game?.id, hasAccess, isStartingGame]); // Removed localGameStatus from deps to prevent re-running

  // Real-time subscription for timer synchronization
  useEffect(() => {
    if (!game?.id || !hasAccess) return;

    console.log('🔔 Setting up real-time timer subscription for game:', game.id);
    
    const channel = supabase
      .channel(`game-timer-${game.id}`)
      .on('postgres_changes',
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'games',
          filter: `id=eq.${game.id}`
        },
        (payload) => {
          // CRITICAL: Ignore real-time updates if we're exiting (use ref to get latest value)
          if (isExitingRef.current) {
            console.log('[REALTIME] Ignoring update - component is exiting');
            return;
          }
          
          console.log('[REALTIME] Game updated from database:', payload.new);
          console.log('[REALTIME] Old values:', payload.old);
          
          // Check if timer_started_at was updated (either newly set or changed)
          const timerWasUpdated = payload.new.timer_started_at && 
            (payload.new.timer_started_at !== payload.old?.timer_started_at || !payload.old?.timer_started_at);
          
          if (timerWasUpdated) {
            console.log('[REALTIME] ⏰ Timer started! Triggering immediate sync...', {
              new: payload.new.timer_started_at,
              old: payload.old?.timer_started_at,
              wasNull: !payload.old?.timer_started_at
            });
            // Update local status to 'started' when timer starts
            if (payload.new.game_status === 'started' || payload.new.timer_started_at) {
              console.log('[REALTIME] Updating localGameStatus to "started"');
              setLocalGameStatus('started');
            }
            syncTimerFromDatabase(payload.new);
          }
          
          // Also check if timer_duration_seconds or current_session_index changed
          if (payload.new.timer_duration_seconds !== payload.old?.timer_duration_seconds ||
              payload.new.current_session_index !== payload.old?.current_session_index) {
            console.log('⏰ Timer duration or session index changed, syncing...');
            syncTimerFromDatabase(payload.new);
          }
          
          // Check if current_session_index changed
          if (payload.new.current_session_index !== undefined && 
              payload.new.current_session_index !== currentSessionIndex) {
            console.log('🔄 Session index changed in database:', payload.new.current_session_index);
            setCurrentSessionIndex(payload.new.current_session_index);
          }
          
          // Check if game status changed
          if (payload.new.game_status !== payload.old?.game_status) {
            // If timer has started, ignore any status changes back to draft/published (invalid state)
            if (payload.new.timer_started_at && (payload.new.game_status === 'draft' || payload.new.game_status === 'published')) {
              console.log('[REALTIME] Ignoring invalid status change - timer already started. Status:', payload.new.game_status);
              console.log('[REALTIME] Old status:', payload.old?.game_status, 'New status:', payload.new.game_status);
              return;
            }
            
            // Don't allow reverting from 'started' to earlier states
            if (payload.old?.game_status === 'started' && (payload.new.game_status === 'draft' || payload.new.game_status === 'published')) {
              console.log('[REALTIME] Blocked: Cannot revert from "started" to "' + payload.new.game_status + '"');
              console.log('[REALTIME] This is likely a database inconsistency - ignoring invalid status change');
              return;
            }
            
            console.log('[REALTIME] 🔄 Game status changed from', payload.old?.game_status, 'to', payload.new.game_status);
            console.log('[STATUS-UPDATE] Changing status from', payload.old?.game_status, 'to', payload.new.game_status, 'at', new Date().toISOString());
            setLocalGameStatus(payload.new.game_status);
            
            // If timer started and status changed to 'started', sync timer
            if (payload.new.game_status === 'started' && payload.new.timer_started_at && !timerStarted) {
              console.log('[REALTIME] Status changed to "started" with timer_started_at, syncing timer...');
              syncTimerFromDatabase(payload.new);
            }
            
            // If game was reverted to draft, reset state
            // If game status changed to 'finished', set allSessionsComplete to true
            if (payload.new.game_status === 'finished' && payload.old?.game_status !== 'finished') {
              console.log('[REALTIME] ✅ Game finished - setting allSessionsComplete to true');
              setAllSessionsComplete(true);
            }
            
            if (payload.new.game_status === 'draft' && (payload.old?.game_status === 'started' || payload.old?.game_status === 'published')) {
              console.log('[REALTIME] 🔄 Game status changed from started to draft - resetting state');
              // Reset all timer and session state
              setTimerStarted(false);
              setTimeRemaining(null);
              setCompletedSessions(new Set());
              setAllSessionsComplete(false);
              setCurrentSessionIndex(0);
              // Reset voice state
              setIsVoicePlaying(false);
              if (currentAudioUrl) {
                URL.revokeObjectURL(currentAudioUrl);
                setCurrentAudioUrl(null);
              }
              // Clear voice timeout if exists
              if (voiceTimeoutRef.current) {
                clearTimeout(voiceTimeoutRef.current);
                voiceTimeoutRef.current = null;
              }
              isPlayingAudioRef.current = false;
              // Reset voice queue and played IDs
              voiceQueueRef.current = [];
              playedVoiceIdsRef.current.clear();
              // Clear intervals
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
                hasStartedIntervalRef.current = false;
              }
              if (voiceCheckIntervalRef.current) {
                clearInterval(voiceCheckIntervalRef.current);
                voiceCheckIntervalRef.current = null;
              }
            }
          }
        }
      )
      .subscribe();

    return () => {
      console.log('🔕 Unsubscribing from timer channel');
      channel.unsubscribe();
    };
  }, [game?.id, hasAccess, currentSessionIndex]);

  // Real-time presence tracking - track who's actively viewing the game
  useEffect(() => {
    if (!game?.id || !hasAccess) return;

    console.log('👥 Setting up presence tracking for game:', game.id);
    
    // Get current user identifier (user_id for authenticated, email for email-only)
    const currentUserId = user?.id || null;
    const currentUserEmail = user?.email || null;
    
    let presenceChannel = null;
    
    // First, try to get player info from game_participants (most reliable source)
    const getPlayerInfo = async () => {
      let currentPlayerName = null;
      let currentPlayerEmail = null;
      try {
        const { data: participantData } = await supabase
          .from('game_participants')
          .select('player_name, email')
          .eq('game_id', game.id)
          .or(`user_id.eq.${currentUserId},email.eq.${currentUserEmail}`)
          .limit(1)
          .single();
        
        if (participantData) {
          currentPlayerName = participantData.player_name || currentUserEmail || 'Player';
          currentPlayerEmail = participantData.email || currentUserEmail;
        }
      } catch (error) {
        console.log('👥 [PRESENCE] Could not fetch participant data:', error);
      }
      
      // Fallback: Try to get player info from designers list or user profile
      if (!currentPlayerName) {
        if (currentUserId) {
          const currentDesigner = designers.find(d => d.userId === currentUserId);
          if (currentDesigner) {
            currentPlayerName = currentDesigner.name;
            currentPlayerEmail = currentDesigner.email || currentUserEmail;
          } else if (userProfile?.full_name) {
            currentPlayerName = userProfile.full_name;
            currentPlayerEmail = userProfile.email || currentUserEmail;
          } else {
            currentPlayerName = currentUserEmail || 'Player';
            currentPlayerEmail = currentUserEmail;
          }
        } else {
          // For email-only players, try to find their entry in designers list
          const currentDesigner = designers.find(d => d.email === currentUserEmail);
          if (currentDesigner) {
            currentPlayerName = currentDesigner.name;
            currentPlayerEmail = currentDesigner.email;
          } else {
            // Fallback: use email as name
            currentPlayerName = currentUserEmail || 'Player';
            currentPlayerEmail = currentUserEmail;
          }
        }
      }
      
      return { currentPlayerName, currentPlayerEmail };
    };
    
    // Set up presence channel (will update presence data after getting player info)
    const setupPresence = async () => {
      const { currentPlayerName: name, currentPlayerEmail: email } = await getPlayerInfo();
      
      const channel = supabase
        .channel(`game-presence-${game.id}`, {
          config: {
            presence: {
              key: currentUserId || email || 'anonymous',
            },
          },
        })
        .on('presence', { event: 'sync' }, () => {
          const state = channel.presenceState();
          console.log('👥 [PRESENCE] Sync event - current presence state:', state);
          
          // Extract active user IDs and emails from presence state
          const activeIds = new Set();
          const activeEmails = new Set();
          
          Object.values(state).forEach((presences) => {
            presences.forEach((presence) => {
              if (presence.user_id) {
                activeIds.add(presence.user_id);
              }
              if (presence.email) {
                activeEmails.add(presence.email);
              }
            });
          });
          
          console.log('👥 [PRESENCE] Active user IDs:', Array.from(activeIds));
          console.log('👥 [PRESENCE] Active emails:', Array.from(activeEmails));
          
          setActivePlayerIds(activeIds);
          setActivePlayerEmails(activeEmails);
        })
        .on('presence', { event: 'join' }, ({ key, newPresences }) => {
          console.log('👥 [PRESENCE] User joined:', key, newPresences);
          const state = channel.presenceState();
          const activeIds = new Set();
          const activeEmails = new Set();
          
          Object.values(state).forEach((presences) => {
            presences.forEach((presence) => {
              if (presence.user_id) {
                activeIds.add(presence.user_id);
              }
              if (presence.email) {
                activeEmails.add(presence.email);
              }
            });
          });
          
          setActivePlayerIds(activeIds);
          setActivePlayerEmails(activeEmails);
        })
        .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
          console.log('👥 [PRESENCE] User left:', key, leftPresences);
          // Add a grace period before removing (5 seconds)
          setTimeout(() => {
            const state = channel.presenceState();
            const activeIds = new Set();
            const activeEmails = new Set();
            
            Object.values(state).forEach((presences) => {
              presences.forEach((presence) => {
                if (presence.user_id) {
                  activeIds.add(presence.user_id);
                }
                if (presence.email) {
                  activeEmails.add(presence.email);
                }
              });
            });
            
            setActivePlayerIds(activeIds);
            setActivePlayerEmails(activeEmails);
          }, 5000); // 5 second grace period
        })
        .subscribe(async (status) => {
          if (status === 'SUBSCRIBED') {
            console.log('👥 [PRESENCE] Subscribed to presence channel');
            
            // Track current user's presence
            const presenceData = {
              user_id: currentUserId,
              email: email || currentUserEmail,
              player_name: name,
              joined_at: new Date().toISOString(),
            };
            
            await channel.track(presenceData);
            console.log('👥 [PRESENCE] Tracking current user presence:', presenceData);
          }
        });
      
      presenceChannel = channel;
      return channel;
    };
    
    setupPresence().catch(err => {
      console.error('👥 [PRESENCE] Error setting up presence:', err);
    });

    // Cleanup on unmount
    return () => {
      console.log('👥 [PRESENCE] Cleaning up presence channel');
      if (presenceChannel) {
        presenceChannel.untrack();
        presenceChannel.unsubscribe();
      }
    };
  }, [game?.id, hasAccess, user?.id, user?.email, userProfile, designers]);

  // Real-time subscription for game_participants to update join status
  useEffect(() => {
    if (!game?.id || !hasAccess) return;

    console.log('🔔 Setting up real-time participant subscription for game:', game.id);
    
    const participantChannel = supabase
      .channel(`game-participants-${game.id}`)
      .on('postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'game_participants',
          filter: `game_id=eq.${game.id}`
        },
        async (payload) => {
          console.log('🔔🔔🔔 [REALTIME] INSERT EVENT FIRED! 🔔🔔🔔');
          console.log('[REALTIME] New participant joined:', payload.new);
          console.log('[REALTIME] Full payload:', JSON.stringify(payload, null, 2));
          const newParticipant = payload.new;
          const newUserId = newParticipant.user_id;
          const newEmail = newParticipant.email;
          const newPlayerName = newParticipant.player_name;
          console.log('[REALTIME] Extracted values:', { newUserId, newEmail, newPlayerName });
          
          // Update participant user IDs set immediately
          if (newUserId) {
            setParticipantUserIds(prev => {
              const updated = new Set(prev);
              updated.add(newUserId);
              console.log('[REALTIME] Updated participantUserIds:', Array.from(updated));
              return updated;
            });
          }
          
          // Reload game data immediately to ensure designers list is updated
          // This is more reliable than manually updating the list
          console.log('[REALTIME] Reloading game data to refresh designers list...');
          loadGameData();
          
          // Also handle email-only participants immediately for faster UI update
          if (!newUserId && newEmail && newPlayerName) {
            console.log('[REALTIME] Email-only participant joined:', newPlayerName, newEmail);
            setDesigners(prevDesigners => {
              // Check if this participant is already in the list
              const alreadyExists = prevDesigners.some(d => 
                (typeof d === 'object' && (d.email === newEmail || d.name === newPlayerName)) ||
                (typeof d === 'string' && d === newPlayerName)
              );

              if (alreadyExists) {
                console.log('[REALTIME] Participant already in list, skipping');
                return prevDesigners;
              }

              // Add email-only participant to designers list
              const updatedDesigners = [...prevDesigners, {
                name: newPlayerName,
                email: newEmail,
                userId: null,
                avatarUrl: null,
                isParticipant: true
              }];

              console.log('[REALTIME] ✅ Added email-only participant to designers list:', newPlayerName);
              return updatedDesigners;
            });
          }
          
          // Note: For authenticated participants, loadGameData() above will handle updating the designers list
          // The code below is kept for backwards compatibility but loadGameData() is the primary method
          if (false && newUserId) {
          // Update participant user IDs set
          setParticipantUserIds(prev => {
            const updated = new Set(prev);
            updated.add(newUserId);
            console.log('[REALTIME] Updated participantUserIds:', Array.from(updated));
            return updated;
          });
          
          // Reload participants to refresh designer list with new participant info
          try {
            const { data: participants, error: participantsError } = await supabase
              .from('game_participants')
                .select('user_id, email, player_name')
              .eq('game_id', game.id);

            if (!participantsError && participants && participants.length > 0) {
              // Filter out null user_ids for the participantUserIds set
              const userIds = participants.filter(p => p.user_id).map(p => p.user_id);
              setParticipantUserIds(new Set(userIds));
              
              // Reload game data to refresh designer list with participant info
              // Only fetch profiles for authenticated participants (filter out null user_ids)
              const userIdsToFetch = participants.filter(p => p.user_id).map(p => p.user_id);
              
              let userProfiles = [];
              if (userIdsToFetch.length > 0) {
                const { data: profilesData, error: profilesError } = await supabase
                .from('user_profiles')
                .select('id, full_name, email')
                  .in('id', userIdsToFetch);

              if (profilesError) {
                console.error('[REALTIME] Error fetching user profiles (406/403):', profilesError);
                console.log('[REALTIME] Falling back to loadGameData() to refresh designers...');
                // Fallback: reload game data which handles participants differently
                loadGameData();
                return;
                }
                
                if (profilesData) {
                  userProfiles = profilesData;
                }
              }

              if (userProfiles && userProfiles.length > 0) {
                setDesigners(prevDesigners => {
                  const profileMap = new Map(userProfiles.map(profile => [profile.id, profile]));
                  // Track which user IDs have been matched to existing designers
                  const matchedUserIds = new Set();
                  
                  // First pass: Update existing designers
                  const updatedDesigners = prevDesigners.map(designer => {
                    const designerName = typeof designer === 'string' ? designer : designer.name;
                    
                    // Try to find matching profile
                    const matchingProfile = Array.from(profileMap.values()).find(profile => {
                      // Skip if this profile is already matched
                      if (matchedUserIds.has(profile.id)) return false;
                      
                      const profileNameLower = profile.full_name?.toLowerCase().trim();
                      const profileEmailLower = profile.email?.toLowerCase().trim();
                      const designerNameLower = designerName.toLowerCase().trim();
                      const designerEmailLower = typeof designer === 'object' ? designer.email?.toLowerCase().trim() : null;
                      
                      // Check for existing match by ID
                      if (typeof designer === 'object' && designer.userId === profile.id) {
                        return true;
                      }
                      
                      // Check by name
                      if (profileNameLower === designerNameLower) return true;
                      
                      // Check by email
                      if (designerEmailLower && profileEmailLower && designerEmailLower === profileEmailLower) return true;
                      
                      // Partial matches (similar to loadGameData)
                      if (designerNameLower && profileNameLower) {
                        // Check if first name matches
                        const designerParts = designerNameLower.split(' ');
                        const profileParts = profileNameLower.split(' ');
                        if (designerParts[0] && profileParts[0] && designerParts[0] === profileParts[0]) return true;
                      }
                      
                      return false;
                    });
                    
                    if (matchingProfile) {
                      matchedUserIds.add(matchingProfile.id);
                      const fullParticipantName = matchingProfile.full_name || matchingProfile.email || 'Unknown';
                      return {
                        ...(typeof designer === 'string' ? { name: designer } : designer),
                        userId: matchingProfile.id,
                        // Update name to match profile if it was just a placeholder
                        name: typeof designer === 'string' ? fullParticipantName : (designer.name || fullParticipantName),
                        email: matchingProfile.email || (typeof designer === 'object' ? designer.email : null)
                      };
                    }
                    return designer;
                  });

                  // Second pass: Add any participants that weren't matched to existing designers
                  userProfiles.forEach(profile => {
                    if (!matchedUserIds.has(profile.id)) {
                      console.log('[REALTIME] Adding new designer from participant:', profile);
                      const fullParticipantName = profile.full_name || profile.email || 'Unknown';
                      const nameParts = fullParticipantName.split(' ');
                      updatedDesigners.push({
                        name: fullParticipantName,
                        firstName: nameParts[0] || '',
                        lastName: nameParts.slice(1).join(' ') || '',
                        email: profile.email || null,
                        avatarUrl: null,
                        userId: profile.id,
                        isParticipant: true
                      });
                    }
                  });

                  console.log('[REALTIME] ✅ Updated designers list with new participant:', updatedDesigners.length, 'designers');
                  return updatedDesigners;
                });
              }
            } else {
              // No participants found or error - reload game data as fallback
              console.log('[REALTIME] No participants found or error, reloading game data...');
              loadGameData();
            }
            
            console.log('[REALTIME] ✅ Participant processing complete');
          } catch (error) {
            console.error('[REALTIME] Error reloading participants:', error);
            // Fallback: try to reload game data which handles participants differently
            console.log('[REALTIME] Attempting fallback loadGameData()...');
            try {
              loadGameData();
            } catch (fallbackError) {
              console.error('[REALTIME] Fallback also failed:', fallbackError);
              }
            }
          }
        }
      )
      .on('postgres_changes',
        { 
          event: 'DELETE', 
          schema: 'public', 
          table: 'game_participants',
          filter: `game_id=eq.${game.id}`
        },
        async (payload) => {
          console.log('[REALTIME] Participant left:', payload.old);
          const removedUserId = payload.old.user_id;
          
          // Update participant user IDs set
          setParticipantUserIds(prev => {
            const updated = new Set(prev);
            updated.delete(removedUserId);
            console.log('[REALTIME] Updated participantUserIds after removal:', Array.from(updated));
            return updated;
          });
          
          // Reload participants
          try {
            const { data: participants } = await supabase
              .from('game_participants')
              .select('user_id')
              .eq('game_id', game.id);

            if (participants) {
              setParticipantUserIds(new Set(participants.map(p => p.user_id)));
            }
          } catch (error) {
            console.error('[REALTIME] Error reloading participants:', error);
          }
        }
      )
      .subscribe();

    // Subscribe to the channel
    participantChannel.subscribe((status) => {
      console.log('🔔 [REALTIME] Subscription status:', status);
      if (status === 'SUBSCRIBED') {
        console.log('✅ [REALTIME] Successfully subscribed to participant channel');
      } else if (status === 'CHANNEL_ERROR') {
        console.error('❌ [REALTIME] Channel subscription error');
      }
    });

    return () => {
      console.log('🔕 Unsubscribing from participant channel');
      participantChannel.unsubscribe();
    };
  }, [game?.id, hasAccess]);

  // Polling fallback to check for new participants (in case real-time fails or 406 errors)
  useEffect(() => {
    if (!game?.id || !hasAccess) return;

    const pollInterval = setInterval(async () => {
      try {
        const { data: participants, error } = await supabase
          .from('game_participants')
          .select('user_id, email, player_name')
          .eq('game_id', game.id);

        if (!error && participants) {
          const currentCount = participants.length;
          const currentParticipantIds = new Set(participants.filter(p => p.user_id).map(p => p.user_id));
          const emailOnlyParticipants = participants.filter(p => !p.user_id && (p.email || p.player_name));
          
          console.log('[POLLING] Current participants:', currentCount, participants);
          console.log('[POLLING] Authenticated participants:', Array.from(currentParticipantIds));
          console.log('[POLLING] Email-only participants:', emailOnlyParticipants);
          console.log('[POLLING] Known participant count:', knownParticipantCount);
          
          // Check if there are new participants (by count or by user_id)
          const hasNewAuthParticipants = Array.from(currentParticipantIds).some(id => !participantUserIds.has(id));
          const hasNewEmailOnlyParticipants = emailOnlyParticipants.length > 0;
          const hasNewCount = currentCount > knownParticipantCount;
          
          if (hasNewAuthParticipants || hasNewEmailOnlyParticipants || hasNewCount) {
            console.log('[POLLING] 🔄 New participants detected! Reloading game data...', {
              hasNewAuthParticipants,
              hasNewEmailOnlyParticipants,
              hasNewCount,
              currentCount,
              knownCount: knownParticipantCount
            });
              // Reload game data to refresh designer list
              loadGameData();
            // Update known count
            setKnownParticipantCount(currentCount);
            // Update participant user IDs
            setParticipantUserIds(currentParticipantIds);
          } else {
            // Still update the counts to track current state
            setKnownParticipantCount(currentCount);
            setParticipantUserIds(currentParticipantIds);
          }
        } else if (error) {
          console.error('[POLLING] Error fetching participants:', error);
        }
      } catch (error) {
        console.error('[POLLING] Error checking participants:', error);
      }
    }, 2000); // Poll every 2 seconds for faster detection

    return () => clearInterval(pollInterval);
  }, [game?.id, hasAccess, knownParticipantCount, participantUserIds]);

  // Check and sync timer from database on mount and periodically
  const checkAndSyncTimer = async () => {
    if (!game?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('games')
        .select('timer_started_at, current_session_index, timer_duration_seconds')
        .eq('id', game.id)
        .single();
      
      if (error) {
        console.error('Error checking timer:', error);
        return;
      }
      
      if (data?.timer_started_at) {
        console.log('⏰ Found existing timer in database, syncing...');
        syncTimerFromDatabase(data);
      } else if (timerStarted) {
        // Timer was running locally but not in database - reset local state
        console.log('⚠️ Timer running locally but not in database - resetting');
        setTimerStarted(false);
        setTimeRemaining(null);
      }
    } catch (error) {
      console.error('Error checking timer:', error);
    }
  };

  // Polling backup for game updates (in case real-time fails)
  useEffect(() => {
    if (!game?.id || !hasAccess) return;
    
    const pollGameUpdates = async () => {
      // CRITICAL: Don't poll if we're exiting (use ref to get latest value)
      if (isExitingRef.current) {
        console.log('[POLLING] Ignoring poll - component is exiting');
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('games')
          .select('*')
          .eq('id', game.id)
          .single();
        
        if (error) {
          console.error('[POLLING] Error fetching game:', error);
          return;
        }
        
        if (!data) return;
        
        // Check if timer_started_at changed
        if (data.timer_started_at !== game?.timer_started_at) {
          const timerJustStarted = data.timer_started_at && !game?.timer_started_at;
          console.log('[POLLING] ⏰ Timer started detected! Updating game state...', {
            wasNull: !game?.timer_started_at,
            now: data.timer_started_at,
            timerJustStarted
          });
          
          // Update local status when timer starts
          if (data.game_status === 'started' || data.timer_started_at) {
            console.log('[POLLING] Updating localGameStatus to "started"');
            setLocalGameStatus('started');
          }
          
          // Sync timer immediately
          if (data.timer_started_at) {
            syncTimerFromDatabase(data);
          }
        }
        
        // Check if game status changed
        if (data.game_status !== game?.game_status) {
          console.log('[POLLING] 🔄 Game status changed detected:', {
            old: game?.game_status,
            new: data.game_status,
            currentLocalStatus: localGameStatus
          });
          
          // CRITICAL FIX: Only update if it's a valid forward transition (prevents flicker)
          // Valid transitions: draft → published → started → finished
          const validTransitions = {
            'draft': ['published'],
            'published': ['started'],
            'started': ['finished'],
            'finished': [] // No transitions from finished
          };
          
          const allowedNextStatuses = validTransitions[localGameStatus] || [];
          if (allowedNextStatuses.includes(data.game_status)) {
            console.log('[POLLING] ✅ Valid transition, updating localGameStatus:', localGameStatus, '→', data.game_status);
            setLocalGameStatus(data.game_status);
          } else if (localGameStatus === 'published' && data.game_status === 'draft') {
            console.log('[POLLING] ⚠️ Blocked invalid revert: "published" → "draft" (prevents flicker)');
          } else {
            console.log('[POLLING] ⚠️ Blocked invalid transition:', localGameStatus, '→', data.game_status);
          }
        }
        
        // Check if session index changed
        if (data.current_session_index !== undefined && 
            data.current_session_index !== game?.current_session_index) {
          console.log('[POLLING] 🔄 Session index changed, updating...', {
            old: game?.current_session_index,
            new: data.current_session_index
          });
          setCurrentSessionIndex(data.current_session_index);
        }
      } catch (error) {
        console.error('[POLLING] Exception in pollGameUpdates:', error);
      }
    };
    
    // Poll every 2 seconds
    const pollInterval = setInterval(pollGameUpdates, 2000);
    
    // Also poll immediately
    pollGameUpdates();
    
    return () => clearInterval(pollInterval);
  }, [game?.id, hasAccess, game?.timer_started_at, game?.game_status, game?.current_session_index]);

  // Watch for timer_started_at changes and sync immediately (works for both host and designers)
  useEffect(() => {
    if (!game?.id || !hasAccess) return;
    
    // If timer_started_at is set but timer hasn't started locally, sync immediately
    if (game?.timer_started_at && !timerStarted) {
      console.log('[TIMER-SYNC] timer_started_at detected, syncing timer for designer/host');
      checkAndSyncTimer();
      
      // CRITICAL: When timer starts, ensure sessions and clientChanges are loaded
      // This ensures voice scheduler has the data it needs
      console.log('[TIMER-SYNC] 🔄 Timer started - ensuring sessions and clientChanges are loaded...', {
        sessionsLength: sessions.length,
        clientChangesLength: clientChanges.length,
        currentSessionIndex
      });
      if (sessions.length === 0) {
        console.log('[TIMER-SYNC] ⚠️ Sessions not loaded, calling loadGameData()...');
        loadGameData();
      } else if (sessions.length > 0 && clientChanges.length === 0) {
        // Sessions are loaded but clientChanges aren't - trigger loadSessionChanges
        console.log('[TIMER-SYNC] ⚠️ Sessions loaded but clientChanges not loaded, calling loadSessionChanges()...');
        loadSessionChanges();
      }
    }
  }, [game?.timer_started_at, game?.id, hasAccess, timerStarted, sessions.length]);

  // Polling fallback for voice clips (in case real-time subscription fails)
  // Only play clips were created AFTER we joined (not old clips from before)
  const playerJoinedTimeRef = useRef(Date.now());
  
  useEffect(() => {
    if (!game?.id || sessions.length === 0) return;
    
    const currentSession = sessions[currentSessionIndex];
    if (!currentSession) return;
    
    // Reset join time when session changes
    playerJoinedTimeRef.current = Date.now();
    
    console.log('[CLIENT] 🔄 Setting up voice clips polling fallback...');
    
    // Poll every 2 seconds to check for new voice clips
    const voiceClipPollInterval = setInterval(async () => {
      try {
        const { data: recentClips, error } = await supabase
          .from('voice_clips')
          .select('*')
          .eq('game_id', game.id)
          .eq('session_name', currentSession.session_name || currentSession.sessionKey)
          .order('created_at', { ascending: false })
          .limit(5);
        
        if (error) {
          console.error('[CLIENT] ❌ Error polling voice clips:', error);
          return;
        }
        
        if (recentClips && recentClips.length > 0) {
          // Only play clips created AFTER we joined (prevent playing old clips)
          recentClips.forEach(clip => {
            const clipCreatedTime = new Date(clip.created_at).getTime();
            const clipId = `${clip.game_id}_${clip.session_name}_${clip.change_index}`;
            
            // Only play if clip was created after we joined AND we haven't played it yet
            if (clipCreatedTime >= playerJoinedTimeRef.current && !playedVoiceIdsRef.current.has(clipId)) {
              console.log('[CLIENT] 🔄 Polling found new unplayed voice clip:', clipId);
              console.log('[CLIENT] 🔄 Clip created at:', new Date(clip.created_at).toISOString());
              console.log('[CLIENT] 🔄 Player joined at:', new Date(playerJoinedTimeRef.current).toISOString());
              console.log('[CLIENT] 🔄 Playing via polling fallback...');
              playVoiceClipFromUrl(clip);
            } else if (clipCreatedTime < playerJoinedTimeRef.current) {
              console.log('[CLIENT] ⏸️ Skipping old voice clip (created before player joined):', clipId);
            }
          });
        }
      } catch (error) {
        console.error('[CLIENT] ❌ Exception in voice clips polling:', error);
      }
    }, 2000); // Poll every 2 seconds
    
    return () => {
      console.log('[CLIENT] Clearing voice clips polling interval');
      clearInterval(voiceClipPollInterval);
    };
  }, [game?.id, currentSessionIndex, sessions]);

  // Poll database periodically to catch timer starts (backup to real-time subscription)
  useEffect(() => {
    if (!game?.id || !hasAccess) return;
    
    // Check every 2 seconds if timer hasn't started locally but might have started in database
    const pollInterval = setInterval(() => {
      if (!timerStarted && (game?.game_status === 'started' || game?.game_status === 'published')) {
        checkAndSyncTimer();
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [game?.id, hasAccess, timerStarted, game?.game_status]);

  // Sync timer from database values
  const syncTimerFromDatabase = (gameData) => {
    if (!gameData?.timer_started_at || !gameData?.timer_duration_seconds) {
      console.log('⚠️ Missing timer data in database - resetting timer state');
      // Reset timer state if no timer is running - this prevents false "Session Complete" messages
      setTimerStarted(false);
      setTimeRemaining(null);
      setCompletedSessions(new Set()); // Reset completed sessions when timer is cleared
      // Reset voice state when timer is cleared
      setIsVoicePlaying(false);
      if (currentAudioUrl) {
        URL.revokeObjectURL(currentAudioUrl);
        setCurrentAudioUrl(null);
      }
      // Clear voice timeout if exists
      if (voiceTimeoutRef.current) {
        clearTimeout(voiceTimeoutRef.current);
        voiceTimeoutRef.current = null;
      }
      isPlayingAudioRef.current = false;
      return;
    }

    const timerStartTime = new Date(gameData.timer_started_at);
    const now = new Date();
    const elapsed = Math.floor((now - timerStartTime) / 1000); // elapsed seconds
    const duration = gameData.timer_duration_seconds;
    const remaining = Math.max(0, duration - elapsed);
    
    console.log('⏰ Syncing timer:', {
      startTime: timerStartTime.toISOString(),
      now: now.toISOString(),
      elapsed,
      duration,
      remaining
    });

    // Update session index if it changed
    if (gameData.current_session_index !== undefined && 
        gameData.current_session_index !== currentSessionIndex) {
      setCurrentSessionIndex(gameData.current_session_index);
      // Reset completed sessions when session changes
      setCompletedSessions(new Set());
      // Reset voice state when session changes
      setIsVoicePlaying(false);
      if (currentAudioUrl) {
        URL.revokeObjectURL(currentAudioUrl);
        setCurrentAudioUrl(null);
      }
      // Clear voice timeout if exists
      if (voiceTimeoutRef.current) {
        clearTimeout(voiceTimeoutRef.current);
        voiceTimeoutRef.current = null;
      }
      isPlayingAudioRef.current = false;
      // Reset voice queue and played IDs when session changes
      voiceQueueRef.current = [];
      playedVoiceIdsRef.current.clear();
    }

    if (remaining > 0) {
      // Timer is still running
      setTimeRemaining(remaining);
      setTimerStarted(true);
    } else {
      // Timer has ended - mark as complete
      console.log('⏰ Timer has already ended - marking session as complete');
      setTimeRemaining(0);
      setTimerStarted(false);
      // Reset voice state when timer ends
      setIsVoicePlaying(false);
      if (currentAudioUrl) {
        URL.revokeObjectURL(currentAudioUrl);
        setCurrentAudioUrl(null);
      }
      isPlayingAudioRef.current = false;
      // Reset voice queue and played IDs when timer ends
      voiceQueueRef.current = [];
      playedVoiceIdsRef.current.clear();
      // Mark session as complete if timer ended
      if (sessions.length > 0 && currentSessionIndex < sessions.length) {
        const currentSession = sessions[currentSessionIndex];
        if (currentSession) {
          console.log('✅ Adding session to completedSessions:', currentSession.sessionKey);
          setCompletedSessions(prev => new Set([...prev, currentSession.sessionKey]));
        }
      }
    }
  };

  // Load submissions for current session
  const loadSubmissions = useCallback(async () => {
    if (!game?.id || sessions.length === 0 || currentSessionIndex >= sessions.length) return;
    
    const currentSession = sessions[currentSessionIndex];
    if (!currentSession) return;
    
    try {
      console.log('[SUBMISSIONS] 📋 Loading submissions for session:', {
        sessionKey: currentSession.sessionKey,
        sessionName: currentSession.session_name,
        gameId: game.id,
        currentSessionIndex,
        sessionObject: currentSession
      });
      
      // Try querying with sessionKey first, then fallback to session_name if different
      const sessionNameToQuery = currentSession.sessionKey || currentSession.session_name;
      
      console.log('[SUBMISSIONS] 🔍 Querying with session name:', sessionNameToQuery);
      
      const { data, error } = await supabase
        .from('submissions')
        .select('*')
        .eq('game_id', game.id)
        .eq('session_name', sessionNameToQuery)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('[SUBMISSIONS] ❌ Error loading submissions:', error);
        console.error('[SUBMISSIONS] ❌ Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        return;
      }
      
      console.log('[SUBMISSIONS] 📊 Query result:', {
        dataLength: data?.length || 0,
        data: data,
        sessionNameQueried: sessionNameToQuery
      });
      
      // If no results with sessionKey, try querying all submissions for this game to see what session names exist
      if (!data || data.length === 0) {
        console.log('[SUBMISSIONS] ⚠️ No submissions found with sessionKey, checking all submissions for this game...');
        const { data: allSubmissions, error: allError } = await supabase
          .from('submissions')
          .select('session_name, designer_name, created_at')
          .eq('game_id', game.id);
        
        if (!allError && allSubmissions) {
          console.log('[SUBMISSIONS] 🔍 All submissions for this game:', allSubmissions);
          const uniqueSessionNames = [...new Set(allSubmissions.map(s => s.session_name))];
          console.log('[SUBMISSIONS] 🔍 Unique session names in database:', uniqueSessionNames);
          console.log('[SUBMISSIONS] 🔍 Expected session name:', sessionNameToQuery);
          console.log('[SUBMISSIONS] 🔍 Match?', uniqueSessionNames.includes(sessionNameToQuery));
        }
      }
      
      if (data && data.length > 0) {
        console.log(`[SUBMISSIONS] ✅ Found ${data.length} submission(s) for session ${sessionNameToQuery}`);
        // Load scores for each submission
        const submissionIds = data.map(s => s.id);
        const { data: scoresData, error: scoresError } = await supabase
          .from('scores')
          .select('*')
          .in('submission_id', submissionIds);
        
        if (scoresError) {
          console.error('[SUBMISSIONS] Error loading scores:', scoresError);
        }
        
        // Combine submissions with scores
        const submissionsWithScores = data.map(submission => {
          const scores = {};
          if (scoresData) {
            scoresData
              .filter(score => score.submission_id === submission.id)
              .forEach(score => {
                scores[score.requirement_id] = score.score;
              });
          }
          return {
            id: submission.id,
            designerName: submission.designer_name,
            imageUrl: submission.image_url,
            imagePath: submission.image_path,
            timestamp: submission.created_at,
            scores: scores
          };
        });
        
        console.log('[SUBMISSIONS] ✅ Loaded submissions with scores:', submissionsWithScores);
        console.log('[SUBMISSIONS] ✅ Setting submissions state with', submissionsWithScores.length, 'submission(s)');
        console.log('[SUBMISSIONS] 📋 Submission designer names:', submissionsWithScores.map(s => s.designerName));
        setSubmissions(submissionsWithScores);
      } else {
        console.log('[SUBMISSIONS] ⚠️ No submissions found for session:', currentSession.sessionKey);
        setSubmissions([]);
      }
    } catch (error) {
      console.error('[SUBMISSIONS] Error loading submissions:', error);
    }
  }, [game?.id, sessions, currentSessionIndex]);

  // Load submissions and requirements when session changes
  useEffect(() => {
    if (game?.id && sessions.length > 0 && currentSessionIndex < sessions.length) {
      loadSubmissions();
      loadSessionRequirements();
    }
  }, [game?.id, sessions, currentSessionIndex, loadSubmissions]);

  // Real-time subscription for submissions - updates immediately when players upload designs
  useEffect(() => {
    if (!game?.id || sessions.length === 0 || currentSessionIndex >= sessions.length) return;

    const currentSession = sessions[currentSessionIndex];
    if (!currentSession) return;

    console.log('[SUBMISSIONS] 🔔 Subscribing to submissions for game:', game.id, 'session:', currentSession.sessionKey);

    const submissionsChannel = supabase
      .channel(`submissions-${game.id}-${currentSession.sessionKey}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'submissions',
          filter: `game_id=eq.${game.id}`
        },
        (payload) => {
          console.log('[SUBMISSIONS] 🔔 Real-time event received:', payload.eventType);
          console.log('[SUBMISSIONS] 🔔 Payload data:', {
            new: payload.new,
            old: payload.old,
            eventType: payload.eventType
          });
          
          // Check if this submission is for the current game and session
          const submissionSessionName = payload.new?.session_name || payload.old?.session_name;
          const submissionGameId = payload.new?.game_id || payload.old?.game_id;
          const submissionDesignerName = payload.new?.designer_name || payload.old?.designer_name;
          
          console.log('[SUBMISSIONS] Checking submission match:', {
            submissionSessionName,
            currentSessionKey: currentSession.sessionKey,
            submissionGameId,
            currentGameId: game.id,
            submissionDesignerName,
            matchesSession: submissionSessionName === currentSession.sessionKey,
            matchesGame: submissionGameId === game.id
          });
          
          // Reload immediately if it's for this game and session (like game_participants subscription)
          if (submissionGameId === game.id && submissionSessionName === currentSession.sessionKey) {
            console.log('[SUBMISSIONS] ✅ Submission matches current game and session, reloading immediately...');
            console.log('[SUBMISSIONS] 📤 Designer who uploaded:', submissionDesignerName);
            console.log('[SUBMISSIONS] 📤 Current session key:', currentSession.sessionKey);
            console.log('[SUBMISSIONS] 📤 Current game ID:', game.id);
            // Reload immediately - no delay needed (database transaction is already complete when event fires)
            loadSubmissions().then(() => {
              console.log('[SUBMISSIONS] ✅ loadSubmissions completed after real-time event');
            }).catch((error) => {
              console.error('[SUBMISSIONS] ❌ Error in loadSubmissions:', error);
            });
          } else if (submissionGameId === game.id) {
            // If it's for this game but different session, still reload (loadSubmissions filters by session)
            console.log('[SUBMISSIONS] ✅ Submission is for current game (different session), reloading...');
            loadSubmissions();
          } else {
            console.log('[SUBMISSIONS] ⏸️ Submission is for different game, ignoring');
          }
        }
      )
      .subscribe();

    return () => {
      console.log('[SUBMISSIONS] 🔕 Unsubscribing from submissions');
      supabase.removeChannel(submissionsChannel);
    };
  }, [game?.id, currentSessionIndex, sessions, loadSubmissions]);

  // Polling fallback for submissions (in case real-time fails)
  useEffect(() => {
    if (!game?.id || sessions.length === 0 || currentSessionIndex >= sessions.length || !isHost) return;

    const currentSession = sessions[currentSessionIndex];
    if (!currentSession) return;

    console.log('[SUBMISSIONS] 🔄 Setting up polling fallback for submissions');

    const pollInterval = setInterval(async () => {
      try {
        const sessionNameToQuery = currentSession.sessionKey || currentSession.session_name;
        const { data: submissions, error } = await supabase
          .from('submissions')
          .select('id, designer_name, session_name, created_at')
          .eq('game_id', game.id)
          .eq('session_name', sessionNameToQuery);

        if (!error && submissions) {
          const currentSubmissionIds = new Set(submissions.map(s => s.id));
          
          // Check if there are new submissions by comparing with current state
          setSubmissions(prev => {
            const prevIds = new Set(prev.map(s => s.id));
            const hasNewSubmissions = Array.from(currentSubmissionIds).some(id => !prevIds.has(id));
            
            if (hasNewSubmissions) {
              console.log('[SUBMISSIONS] [POLLING] New submissions detected, reloading...');
              loadSubmissions();
              return prev; // loadSubmissions will update state
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('[SUBMISSIONS] [POLLING] Error checking submissions:', error);
      }
    }, 3000); // Poll every 3 seconds as fallback

    return () => {
      console.log('[SUBMISSIONS] 🔄 Clearing polling fallback');
      clearInterval(pollInterval);
    };
  }, [game?.id, currentSessionIndex, sessions, isHost, loadSubmissions]);

  // Load requirements for current session
  const loadSessionRequirements = async () => {
    if (!game?.id || sessions.length === 0 || currentSessionIndex >= sessions.length) return;
    
    const currentSession = sessions[currentSessionIndex];
    if (!currentSession) return;
    
    try {
      console.log('📋 Loading requirements for session:', currentSession.sessionKey);
      
      const { data, error } = await supabase
        .from('session_requirements')
        .select('*')
        .eq('game_id', game.id)
        .eq('session_name', currentSession.sessionKey)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error loading requirements:', error);
        return;
      }
      
      if (data) {
        console.log('✅ Loaded requirements:', data);
        setSessionRequirements(data.map(req => req.requirement_text));
      } else {
        setSessionRequirements([]);
      }
    } catch (error) {
      console.error('Error loading requirements:', error);
      setSessionRequirements([]);
    }
  };

  // Load client changes and bonus changes when session changes
  useEffect(() => {
    console.log('🔄 loadSessionChanges useEffect triggered:', {
      hasGame: !!game,
      gameId: game?.id,
      sessionsLength: sessions.length,
      currentSessionIndex,
      conditionMet: game && sessions.length > 0 && currentSessionIndex < sessions.length
    });
    if (game && sessions.length > 0 && currentSessionIndex < sessions.length) {
      console.log('✅ Calling loadSessionChanges()');
      loadSessionChanges();
      // Reset read changes when session changes
      setReadChanges(new Set());
    } else {
      console.log('❌ loadSessionChanges useEffect: conditions not met, skipping');
    }
  }, [game, sessions, currentSessionIndex]);

  const loadGameStatus = async () => {
    if (!game?.id) return;
    try {
      const { data: gameData, error } = await supabase
        .from('games')
        .select('game_status')
        .eq('id', game.id)
        .single();
      
      if (error) {
        console.error('Error loading game status:', error);
        return;
      }
      
      if (gameData) {
        console.log('🔄 Loaded game status from DB:', gameData.game_status, '(current prop:', game.game_status, ')');
        // Note: We can't mutate the prop directly, but the parent should update it
        // For now, we'll rely on the parent to pass the correct status
      }
    } catch (error) {
      console.error('Error in loadGameStatus:', error);
    }
  };

  // Removed auto-start useEffect - timer should only start when user clicks "Start Game" or "Start Session" buttons

  const loadResources = async () => {
    if (!game?.id) {
      setResources([]);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('game_id', game.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading resources:', error);
        setResources([]);
      } else if (data) {
        // Process resources to get proper image URLs
        const resourcesWithUrls = await Promise.all(data.map(async (res) => {
          let externalUrl = res.url;
          let imageUrl = null;
          
          if (res.type === 'link') {
            // For link resources: url = external link, file_path = image path
            if (res.file_path) {
              const { data: { publicUrl } } = supabase.storage
                .from('design-submissions')
                .getPublicUrl(res.file_path);
              imageUrl = publicUrl;
            }
    } else {
            // For file resources: url = image URL
            if (res.file_path) {
              const { data: { publicUrl } } = supabase.storage
                .from('design-submissions')
                .getPublicUrl(res.file_path);
              imageUrl = publicUrl;
            }
          }
          
          return {
            ...res,
            imageUrl: imageUrl || res.url,
            externalUrl: res.type === 'link' ? externalUrl : null
          };
        }));
        
        setResources(resourcesWithUrls);
      }
    } catch (error) {
      console.error('Error loading resources:', error);
      setResources([]);
    }
  };

  // Synchronized countdown interval - always calculates from database timestamp
  useEffect(() => {
    console.log('⏱️ Sync countdown useEffect triggered', { 
      timeRemaining, 
      timerStarted, 
      hasStarted: hasStartedIntervalRef.current,
      intervalExists: !!intervalRef.current,
      gameId: game?.id
    });
    
    // Clear existing interval if timer stopped
    if (!timerStarted && intervalRef.current) {
      console.log('🛑 Timer stopped, clearing interval');
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      hasStartedIntervalRef.current = false;
      // Reset voice state when timer stops
      setIsVoicePlaying(false);
      if (currentAudioUrl) {
        URL.revokeObjectURL(currentAudioUrl);
        setCurrentAudioUrl(null);
      }
      // Clear voice timeout if exists
      if (voiceTimeoutRef.current) {
        clearTimeout(voiceTimeoutRef.current);
        voiceTimeoutRef.current = null;
      }
      isPlayingAudioRef.current = false;
      return;
    }
    
    // If timer is started, sync from database every second to keep all clients synchronized
    if (timerStarted && !hasStartedIntervalRef.current && game?.id) {
      console.log('✅✅✅ Starting synchronized countdown interval');
      hasStartedIntervalRef.current = true;
      
      intervalRef.current = setInterval(async () => {
        // Always recalculate from database timestamp for perfect synchronization
        try {
          const queryStartTime = Date.now(); // Measure round-trip time
          
          const { data, error } = await supabase
            .from('games')
            .select('timer_started_at, timer_duration_seconds, current_session_index')
            .eq('id', game.id)
            .single();
          
          const queryEndTime = Date.now();
          const roundTripTime = queryEndTime - queryStartTime;
          
          if (!error && data?.timer_started_at && data?.timer_duration_seconds) {
            const timerStartTime = new Date(data.timer_started_at);
            const timerStartTimeMs = timerStartTime.getTime();
            
            // Get current client time
            const clientTimeNow = Date.now();
            
            // Calculate elapsed time directly from timer_started_at
            // Account for round-trip time: the timer_started_at timestamp was set on the server,
            // so we need to account for the time it took to receive it
            // Use the time when we received the response (queryEndTime) as our reference
            const adjustedClientTime = queryEndTime; // Use query end time as reference
            const elapsed = Math.floor((adjustedClientTime - timerStartTimeMs) / 1000);
            const duration = data.timer_duration_seconds;
            const remaining = Math.max(0, duration - elapsed);
            
            // Debug logging for timer sync (every 5 seconds for easier comparison)
            if (Math.floor(elapsed) % 5 === 0 && Math.floor(elapsed * 10) % 50 === 0) {
              console.log('⏱️ Timer sync calculation:', {
                timerStartTime: timerStartTime.toISOString(),
                timerStartTimeMs: timerStartTimeMs,
                queryStartTime: queryStartTime,
                queryEndTime: queryEndTime,
                roundTripTime: `${roundTripTime}ms`,
                adjustedClientTime: adjustedClientTime,
                adjustedClientTimeISO: new Date(adjustedClientTime).toISOString(),
                elapsed: `${elapsed}s`,
                duration: `${duration}s`,
                remaining: `${remaining}s`,
                formattedRemaining: `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`
              });
            }
            
            // SYNC CHECK - Every 5 seconds
            if (Math.floor(elapsed) % 5 === 0 && Math.floor(elapsed * 10) % 50 === 0) {
              console.log('🔍 SYNC CHECK:', {
                clientRawTime: clientTimeNow,
                queryEndTime: queryEndTime,
                timerStartedAt: timerStartTimeMs,
                elapsedSeconds: elapsed,
                remainingSeconds: remaining,
                timeRemaining: remaining
              });
            }
            
            // Update session index if it changed
            if (data.current_session_index !== undefined && 
                data.current_session_index !== currentSessionIndex) {
              setCurrentSessionIndex(data.current_session_index);
            }
            
            if (remaining > 0) {
              setTimeRemaining(remaining);
            } else {
              // Timer ended
              console.log('⏰ Timer reached 0, stopping');
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
                hasStartedIntervalRef.current = false;
              }
              setTimeRemaining(0);
              setTimerStarted(false);
              // Reset voice state when timer ends
              setIsVoicePlaying(false);
              if (currentAudioUrl) {
                URL.revokeObjectURL(currentAudioUrl);
                setCurrentAudioUrl(null);
              }
              // Clear voice timeout if exists
              if (voiceTimeoutRef.current) {
                clearTimeout(voiceTimeoutRef.current);
                voiceTimeoutRef.current = null;
              }
              isPlayingAudioRef.current = false;
              handleTimerEnd();
            }
          } else {
            // Fallback: decrement local timer if DB sync fails
        setTimeRemaining(prev => {
          if (prev === null || prev === undefined || prev <= 0) {
                console.log('⏰ Timer reached 0 (fallback), stopping');
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
              hasStartedIntervalRef.current = false;
            }
            handleTimerEnd();
            return 0;
          }
              return prev - 1;
            });
          }
        } catch (error) {
          console.error('Error syncing timer from database:', error);
          // Fallback: decrement local timer
          setTimeRemaining(prev => {
            if (prev === null || prev === undefined || prev <= 0) {
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
                hasStartedIntervalRef.current = false;
              }
              handleTimerEnd();
              return 0;
            }
            return prev - 1;
          });
        }
      }, 1000); // Sync every second
    } else if (timerStarted && hasStartedIntervalRef.current) {
      // Interval already running, don't recreate
      console.log('✅ Sync interval already running');
    }

    return () => {
      if (intervalRef.current) {
        console.log('🧹 Component unmounting, clearing sync interval');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        hasStartedIntervalRef.current = false;
      }
    };
  }, [timerStarted, game?.id, currentSessionIndex]); // Sync when timer starts or game/session changes

  const loadGameData = async () => {
    console.log('[LOAD-GAME-DATA] 🔄 Starting loadGameData...', {
      gameId: game?.id,
      hasAccess,
      currentSessionsCount: sessions.length
    });
    try {
      // Load sessions
      const { data: briefs, error } = await supabase
        .from('session_briefs')
        .select('*')
        .eq('game_id', game.id)
        .order('session_name', { ascending: true });

      if (error) {
        console.error('Error loading sessions:', error);
        return;
      }

      console.log('Loaded sessions from DB:', briefs);

      if (briefs && briefs.length > 0) {
        const sessionsData = briefs.map(brief => ({
          id: brief.id,
          sessionKey: brief.session_name,
          name: brief.session_display_name || (brief.session_name === 'sessionA' ? 'Session A' : 'Session B'),
          timerDuration: brief.timer_duration || 15,
          creativeBrief: brief.brief_text || brief.creative_brief,
          targetAudience: brief.target_audience
        }));
        console.log('Processed sessions data:', sessionsData);
        console.log(`[LOAD-GAME-DATA] ✅ Setting sessions state (${sessionsData.length} sessions) - this should trigger voice scheduler`);
        setSessions(sessionsData);
      } else {
        console.log('No sessions found for game:', game.id);
      }

      // Load designers from games.designers field
      const { data: gameData } = await supabase
        .from('games')
        .select('designers')
        .eq('id', game.id)
        .single();

      let normalizedDesigners = [];
      if (gameData?.designers) {
        // Handle both old format (strings) and new format (objects)
        normalizedDesigners = Array.isArray(gameData.designers)
          ? gameData.designers.map(d => {
              if (typeof d === 'string') {
                return { name: d, avatarUrl: null };
              }
              // If it has firstName/lastName, combine them into name for display
              if (d.firstName || d.lastName) {
                return { 
                  name: `${d.firstName || ''} ${d.lastName || ''}`.trim(), 
                  firstName: d.firstName,
                  lastName: d.lastName,
                  email: d.email,
                  avatarUrl: d.avatarUrl || null 
                };
              }
              return d;
            })
          : [];
      }

      // Also load participants who joined via join code (both authenticated and email-only)
      // This is the source of truth for who's actually playing - both host and players should see all participants
      console.log('[LOAD-GAME-DATA] 🔍 Loading participants for game:', game.id);
      const { data: participants, error: participantsError } = await supabase
        .from('game_participants')
        .select('user_id, email, player_name')
        .eq('game_id', game.id);

      console.log('[LOAD-GAME-DATA] 📊 Participants query result:', {
        count: participants?.length || 0,
        participants: participants,
        error: participantsError,
        isHost: isHost()
      });

      // Track participant user IDs for join status (filter out nulls for email-only participants)
      if (participants && participants.length > 0) {
        setParticipantUserIds(new Set(participants.filter(p => p.user_id).map(p => p.user_id)));
        setKnownParticipantCount(participants.length);
      } else {
        setParticipantUserIds(new Set());
        setKnownParticipantCount(0);
      }

      if (participantsError) {
        console.error('❌ [LOAD-GAME-DATA] Error loading participants:', participantsError);
        console.log('[LOAD-GAME-DATA] Participants error details:', JSON.stringify(participantsError, null, 2));
      }

      // For players (non-hosts), use participants as the primary source since they may not have access to games.designers
      // For hosts, merge games.designers with participants
      if (!isHost()) {
        // Players: Build designers list from participants only
        console.log('[LOAD-GAME-DATA] Player view: Building designers list from participants');
        normalizedDesigners = [];
        
        if (participants && participants.length > 0) {
          console.log('[LOAD-GAME-DATA] Player view: Found', participants.length, 'participants');
          console.log('[LOAD-GAME-DATA] Player view: Participant data:', JSON.stringify(participants, null, 2));
          
          // For players, use participant data directly (player_name, email) since RLS prevents viewing other users' profiles
          // Process all participants (both authenticated and email-only) using data from game_participants
          participants.forEach((participant, index) => {
            console.log(`[LOAD-GAME-DATA] Player view: Processing participant ${index + 1}:`, {
              user_id: participant.user_id,
              email: participant.email,
              player_name: participant.player_name
            });
            
            // Determine participant name - prioritize player_name, then email, then fallback
            let participantName = participant.player_name || participant.email || 'Unknown';
            
            // If we have user_id, this is an authenticated participant
            if (participant.user_id) {
              const nameParts = participantName.split(' ');
              
              console.log(`[LOAD-GAME-DATA] Player view: Adding authenticated participant: ${participantName}`);
              normalizedDesigners.push({
                name: participantName,
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || '',
                email: participant.email || null,
                avatarUrl: null,
                userId: participant.user_id,
                isParticipant: true
              });
            } 
            // If no user_id but we have email and/or player_name, this is an email-only participant
            else if (participant.email || participant.player_name) {
              const nameParts = participantName.split(' ');
              console.log(`[LOAD-GAME-DATA] Player view: Adding email-only participant: ${participantName}`);
              normalizedDesigners.push({
                name: participantName,
                email: participant.email || null,
                userId: null,
                avatarUrl: null,
                isParticipant: true
              });
            } 
            // Last resort: if we have absolutely no data, still add them as "Unknown" to ensure they appear
            else {
              console.warn(`[LOAD-GAME-DATA] Player view: Participant ${index + 1} has no identifiable data, adding as Unknown:`, participant);
              normalizedDesigners.push({
                name: 'Unknown',
                email: null,
                userId: participant.user_id || null,
                avatarUrl: null,
                isParticipant: true
              });
            }
          });
          
          console.log('[LOAD-GAME-DATA] Player view: Processed', normalizedDesigners.length, 'participants into designers list');
          console.log('[LOAD-GAME-DATA] Player view: Final designers:', normalizedDesigners.map(d => d.name));
        } else {
          console.log('[LOAD-GAME-DATA] Player view: No participants found');
        }
        
        console.log('[LOAD-GAME-DATA] Player view: Final designers list has', normalizedDesigners.length, 'designers');
        setDesigners(normalizedDesigners);
        return; // Early return for players
      }

      // Host view: Merge games.designers with participants
      // First, get host info to filter them out from participants
      let hostEmail = null;
      let hostUserId = game?.created_by || null;
      if (hostUserId) {
        try {
          const { data: hostProfile } = await supabase
            .from('user_profiles')
            .select('email')
            .eq('id', hostUserId)
            .single();
          if (hostProfile) {
            hostEmail = hostProfile.email?.toLowerCase();
          }
        } catch (error) {
          console.log('[LOAD-GAME-DATA] Could not fetch host email:', error);
        }
      }
      
      if (participants && participants.length > 0) {
        // Filter out the host from participants (host shouldn't appear as a designer)
        const nonHostParticipants = participants.filter(p => {
          // Exclude if user_id matches host's user_id
          if (hostUserId && p.user_id && p.user_id === hostUserId) {
            console.log('[LOAD-GAME-DATA] Host view: Filtering out host participant (by user_id):', p);
            return false;
          }
          // Exclude if email matches host's email
          if (hostEmail && p.email && p.email.toLowerCase() === hostEmail) {
            console.log('[LOAD-GAME-DATA] Host view: Filtering out host participant (by email):', p);
            return false;
          }
          return true;
        });
        
        console.log('[LOAD-GAME-DATA] Host view: Filtered participants (excluding host):', {
          total: participants.length,
          nonHost: nonHostParticipants.length,
          hostUserId,
          hostEmail
        });
        
        // Get user profiles for authenticated participants only (filter out null user_ids and host)
        const userIds = nonHostParticipants.filter(p => p.user_id).map(p => p.user_id);
        
        // Only query user_profiles if we have authenticated participants
        let userProfiles = [];
        if (userIds.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
          .from('user_profiles')
          .select('id, full_name, email')
          .in('id', userIds);

        if (profilesError) {
          console.error('Error loading user profiles:', profilesError);
          } else if (profilesData) {
            userProfiles = profilesData;
          }
        }

        // Process authenticated participants (with user_id)
        // Also handle authenticated participants who don't have profiles (fallback to player_name/email)
        nonHostParticipants.forEach(participant => {
          // Skip email-only participants here (they'll be handled later)
          if (!participant.user_id) return;
          
          // Try to get profile, but fallback to participant data if not found
          let profile = null;
          let participantName = participant.player_name || participant.email || 'Unknown';
          let participantEmail = participant.email || null; // Keep original casing for display
          let participantEmailLower = participant.email?.toLowerCase().trim(); // Lowercase for comparisons
          
          if (userProfiles && userProfiles.length > 0) {
          const profileMap = new Map(userProfiles.map(profile => [profile.id, profile]));
            profile = profileMap.get(participant.user_id);
            
            // If profile found, use it; otherwise fallback to participant data
            if (profile) {
              participantName = profile.full_name || profile.email || participantName;
              participantEmail = profile.email || participantEmail;
              participantEmailLower = profile.email?.toLowerCase().trim() || participantEmailLower;
            } else {
              console.log('[LOAD-GAME-DATA] Host view: ⚠️ No profile found for authenticated participant, using fallback:', {
                user_id: participant.user_id,
                player_name: participant.player_name,
                email: participant.email
              });
            }
          } else {
            console.log('[LOAD-GAME-DATA] Host view: ⚠️ No user profiles loaded, using participant data for:', {
              user_id: participant.user_id,
              player_name: participant.player_name,
              email: participant.email
            });
          }
          
            const participantNameLower = participantName.toLowerCase().trim();

            // Try to find matching designer by name (exact or partial match)
            let matchedDesignerIndex = -1;
            for (let i = 0; i < normalizedDesigners.length; i++) {
              const designer = normalizedDesigners[i];
              const designerName = typeof designer === 'string' ? designer : designer.name;
              const designerNameLower = designerName?.toLowerCase().trim() || '';
              const designerEmail = typeof designer === 'object' ? designer.email?.toLowerCase().trim() : null;

              // Check for exact name match
              if (designerNameLower === participantNameLower) {
                matchedDesignerIndex = i;
                break;
              }
              
              // Check for partial name match (one contains the other)
              if (designerNameLower && participantNameLower) {
                const designerParts = designerNameLower.split(' ').filter(p => p.length > 0);
                const participantParts = participantNameLower.split(' ').filter(p => p.length > 0);
                
                // Check if first name matches (most common case: "Tim" matches "Tim Croxdale")
                if (designerParts.length > 0 && participantParts.length > 0 && 
                    designerParts[0] === participantParts[0]) {
                  matchedDesignerIndex = i;
                  break;
                }
                
                // Check if last name matches
                if (designerParts.length > 0 && participantParts.length > 0 &&
                    designerParts[designerParts.length - 1] === participantParts[participantParts.length - 1]) {
                  matchedDesignerIndex = i;
                  break;
                }
                
                // Check if designer name is contained in participant name (e.g., "Tim" in "Tim Croxdale")
                if (participantNameLower.includes(designerNameLower) && designerNameLower.length >= 2) {
                  matchedDesignerIndex = i;
                  break;
                }
                
                // Check if participant name is contained in designer name (less common)
                if (designerNameLower.includes(participantNameLower) && participantNameLower.length >= 2) {
                  matchedDesignerIndex = i;
                  break;
                }
              }

              // Check for email match (use lowercase for comparison)
              if (designerEmail && participantEmailLower && designerEmail === participantEmailLower) {
                matchedDesignerIndex = i;
                break;
              }
            }

            // If we found a match, update the designer entry with userId
            if (matchedDesignerIndex >= 0) {
              const matchedDesigner = normalizedDesigners[matchedDesignerIndex];
              // Use participantName (which may come from profile or fallback to player_name/email)
              const fullParticipantName = participantName;
              
              if (typeof matchedDesigner === 'object') {
                normalizedDesigners[matchedDesignerIndex] = {
                  ...matchedDesigner,
                  userId: participant.user_id,
                  // Use participantName (from profile if available, otherwise from participant data)
                  name: fullParticipantName,
                  // Preserve firstName/lastName if they exist, but prioritize participantName
                  firstName: matchedDesigner.firstName || fullParticipantName.split(' ')[0] || '',
                  lastName: matchedDesigner.lastName || fullParticipantName.split(' ').slice(1).join(' ') || '',
                  email: participantEmail || matchedDesigner.email
                };
              } else {
                // Convert string to object with userId
                const nameParts = fullParticipantName.split(' ');
                normalizedDesigners[matchedDesignerIndex] = {
                  name: fullParticipantName,
                  firstName: nameParts[0] || '',
                  lastName: nameParts.slice(1).join(' ') || '',
                  userId: participant.user_id,
                  email: participantEmail || null,
                  avatarUrl: null
                };
              }
            } else {
              // No match found - add as new designer
              const fullParticipantName = participantName;
              const nameParts = fullParticipantName.split(' ');
              normalizedDesigners.push({
                name: fullParticipantName,
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || '',
                email: participantEmail || null,
                avatarUrl: null,
                userId: participant.user_id,
                isParticipant: true
              });
              console.log('[LOAD-GAME-DATA] Host view: ✅ Added authenticated participant (with fallback):', {
                name: fullParticipantName,
                user_id: participant.user_id,
                hasProfile: !!profile
              });
            }
          });
        }

      // Add email-only participants (those without user_id) to designers list
      // But exclude the host if they somehow got in as email-only
      if (participants && participants.length > 0) {
        // Filter out host from email-only participants too
        const nonHostParticipants = participants.filter(p => {
          if (hostUserId && p.user_id && p.user_id === hostUserId) return false;
          if (hostEmail && p.email && p.email.toLowerCase() === hostEmail) return false;
          return true;
        });
        const emailOnlyParticipants = nonHostParticipants.filter(p => !p.user_id && (p.email || p.player_name));
        console.log('[LOAD-GAME-DATA] Host view: Found', emailOnlyParticipants.length, 'email-only participants:', emailOnlyParticipants);
        console.log('[LOAD-GAME-DATA] Host view: All participants:', participants);
        for (const emailParticipant of emailOnlyParticipants) {
          console.log('[LOAD-GAME-DATA] Host view: Processing email-only participant:', emailParticipant);
          // Determine name - prioritize player_name, then email, then fallback
          const participantName = emailParticipant.player_name || emailParticipant.email || 'Unknown';
          
          // Check if this email-only participant is already in the designers list
          const alreadyExists = normalizedDesigners.some(d => {
            if (typeof d === 'string') {
              return d === participantName || d === emailParticipant.email;
            }
            // Check by name or email
            return (d.email && emailParticipant.email && d.email.toLowerCase() === emailParticipant.email.toLowerCase()) ||
                   (d.name && d.name === participantName) ||
                   (d.name && emailParticipant.email && d.name.toLowerCase() === emailParticipant.email.toLowerCase());
          });

          if (!alreadyExists) {
            console.log('[LOAD-GAME-DATA] Host view: ✅ Adding email-only participant to designers:', participantName);
            normalizedDesigners.push({
              name: participantName,
              email: emailParticipant.email || null,
              userId: null, // No user_id for email-only participants
              avatarUrl: null,
              isParticipant: true
            });
          } else {
            console.log('[LOAD-GAME-DATA] Host view: ⚠️ Email-only participant already exists, skipping:', participantName);
          }
        }
        console.log('[LOAD-GAME-DATA] Host view: Final designers list after adding email-only participants:', normalizedDesigners.map(d => typeof d === 'string' ? d : d.name));
      }

      // Also ensure host's full name is loaded if host is in designers list
      if (game?.created_by) {
        const { data: hostProfile } = await supabase
          .from('user_profiles')
          .select('id, full_name, email')
          .eq('id', game.created_by)
          .single();
        
        if (hostProfile) {
          // Find and update host's entry in designers list with full name
          const hostIndex = normalizedDesigners.findIndex(d => {
            const designerUserId = typeof d === 'object' ? d.userId : null;
            const designerEmail = typeof d === 'object' ? d.email : null;
            return designerUserId === game.created_by || 
                   designerEmail === hostProfile.email ||
                   (typeof d === 'object' && d.name && hostProfile.full_name && 
                    d.name.toLowerCase().includes(hostProfile.full_name.toLowerCase().split(' ')[0]));
          });
          
          if (hostIndex >= 0) {
            const hostDesigner = normalizedDesigners[hostIndex];
            const hostFullName = hostProfile.full_name || hostProfile.email || 'Host';
            if (typeof hostDesigner === 'object') {
              normalizedDesigners[hostIndex] = {
                ...hostDesigner,
                userId: game.created_by,
                name: hostFullName,
                firstName: hostFullName.split(' ')[0] || '',
                lastName: hostFullName.split(' ').slice(1).join(' ') || '',
                email: hostProfile.email || hostDesigner.email
              };
            } else {
              const nameParts = hostFullName.split(' ');
              normalizedDesigners[hostIndex] = {
                name: hostFullName,
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || '',
                userId: game.created_by,
                email: hostProfile.email || null,
                avatarUrl: null
              };
            }
          }
        }
      }

      setDesigners(normalizedDesigners);
    } catch (error) {
      console.error('Error loading game data:', error);
    }
  };

  // Load voice timing configuration from game_details
  const loadVoiceTimingConfig = async () => {
    if (!game?.id) return;
    
    console.log('⚙️ Loading voice timing config for game:', game.id);
    
    try {
      const { data, error } = await supabase
        .from('game_details')
        .select('voice_timing_config')
        .eq('game_id', game.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('❌ Error loading voice timing config:', error);
        return;
      }

      if (data?.voice_timing_config) {
        try {
          const config = typeof data.voice_timing_config === 'string' 
            ? JSON.parse(data.voice_timing_config)
            : data.voice_timing_config;
          console.log('✅ Loaded voice timing config from DB:', config);
          console.log('📊 Raw DB data:', data.voice_timing_config);
          setVoiceTimingConfig(config);
        } catch (parseError) {
          console.error('❌ Error parsing voice timing config:', parseError);
          console.error('📊 Raw data that failed to parse:', data.voice_timing_config);
        }
      } else {
        // Use default config if none exists
        console.log('⚠️ No voice timing config found in DB, using defaults');
        console.log('📊 Full game_details record:', data);
        setVoiceTimingConfig({
          mode: 'specific',
          values: [2, 5, 8],
          bonusValue: 10
        });
      }
    } catch (error) {
      console.error('❌ Error loading voice timing config:', error);
    }
  };

  // Handle designer upload (for designer view)
  const handleDesignerUpload = async (file, designerName) => {
    if (!file || !game?.id || sessions.length === 0 || currentSessionIndex >= sessions.length) return;
    
    const currentSession = sessions[currentSessionIndex];
    if (!currentSession) return;
    
    setIsUploadingDesignerSubmission(true);
    
    try {
      // Check if there's an existing submission for this designer in this session
      const existingSubmission = submissions.find(
        sub => sub.designerName === designerName
      );
      
      // Delete old submission if it exists
      if (existingSubmission) {
        // Delete old image from storage
        if (existingSubmission.imagePath) {
          try {
            await supabase.storage
              .from('design-submissions')
              .remove([existingSubmission.imagePath]);
          } catch (error) {
            console.error('Error deleting old image:', error);
            // Continue even if deletion fails
          }
        }
        
        // Delete old submission from database
        const { error: deleteError } = await supabase
          .from('submissions')
          .delete()
          .eq('id', existingSubmission.id);
        
        if (deleteError) {
          console.error('Error deleting old submission:', deleteError);
        }
        
        // Remove from local state
        setSubmissions(submissions.filter(sub => sub.id !== existingSubmission.id));
      }
      
      // Upload new image
      const { path, url } = await uploadImage(
        file,
        currentSession.sessionKey,
        designerName
      );
      
      // Find current user's ID and email for binding submissions to participants
      const normalizedDesignerName = designerName?.toLowerCase().trim();
      const currentUserDesigner = designers.find(d => {
        const designer = typeof d === 'string' ? { name: d } : d;
        const nameMatch = designer?.name?.toLowerCase().trim() === normalizedDesignerName;
        const userMatch = user?.id && designer?.userId === user.id;
        return nameMatch || userMatch;
      });
      const userId = currentUserDesigner?.userId || user?.id || null;
      const designerEmail =
        (typeof currentUserDesigner === 'object' && currentUserDesigner?.email) ||
        user?.email ||
        null;
      
      // Save to database with user_id
      const sessionNameForDB = currentSession.sessionKey || currentSession.session_name;
      console.log('[UPLOAD] 💾 Saving submission with:', {
        game_id: game.id,
        session_name: sessionNameForDB,
        designer_name: designerName,
        sessionKey: currentSession.sessionKey,
        session_name_prop: currentSession.session_name
      });
      
      const { data, error } = await supabase
        .from('submissions')
        .insert([{
          game_id: game.id,
          session_name: sessionNameForDB,
          designer_name: designerName,
          image_url: url,
          image_path: path,
          user_id: userId, // Add user_id for tracking
          designer_email: designerEmail || null
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      // Add to local state
      setSubmissions([{
        id: data.id,
        designerName: data.designer_name,
        imageUrl: data.image_url,
        imagePath: data.image_path,
        timestamp: data.created_at,
        scores: {}
      }, ...submissions.filter(sub => sub.designerName !== designerName)]);
      
      console.log('✅ Design uploaded successfully!');
    } catch (error) {
      console.error('Error uploading design:', error);
      alert('Failed to upload design. Please try again.');
    } finally {
      setIsUploadingDesignerSubmission(false);
    }
  };

  // Load client changes and bonus changes for current session
  const loadSessionChanges = async () => {
    console.log('[LOAD-SESSION-CHANGES] 📞 loadSessionChanges called', {
      gameId: game?.id,
      sessionsLength: sessions.length,
      currentSessionIndex,
      currentSession: sessions[currentSessionIndex]?.sessionKey || sessions[currentSessionIndex]?.session_name
    });
    if (!game?.id || sessions.length === 0 || currentSessionIndex >= sessions.length) {
      console.log('[LOAD-SESSION-CHANGES] 🔇 Skipped:', { 
        hasGame: !!game?.id, 
        gameId: game?.id,
        sessionsLength: sessions.length, 
        currentSessionIndex,
        sessions: sessions.map(s => s.sessionKey || s.session_name),
        reason: !game?.id ? 'no game' : sessions.length === 0 ? 'no sessions' : 'invalid session index'
      });
      return;
    }
    
    const currentSession = sessions[currentSessionIndex];
    if (!currentSession) {
      console.log('🔇 loadSessionChanges skipped: no current session', { currentSessionIndex, sessions });
      return;
    }

    console.log('📋 Loading session changes for:', currentSession.sessionKey, currentSession);

    try {
      // Load client changes (non-bonus)
      console.log('🔍 Querying client_changes with:', {
        game_id: game.id,
        session_name: currentSession.sessionKey,
        is_bonus: false
      });
      
      const { data: changes, error: changesError } = await supabase
        .from('client_changes')
        .select('*')
        .eq('game_id', game.id)
        .eq('session_name', currentSession.sessionKey)
        .eq('is_bonus', false)
        .order('order', { ascending: true })
        .order('created_at', { ascending: true }); // Fallback to created_at if order is not set

      console.log('📥 Query result:', { 
        data: changes, 
        error: changesError,
        dataLength: changes?.length,
        hasError: !!changesError
      });

      if (changesError) {
        console.error('❌ Error loading client changes:', changesError);
        console.error('❌ Error details:', {
          message: changesError.message,
          code: changesError.code,
          details: changesError.details,
          hint: changesError.hint
        });
      } else if (changes) {
        console.log(`✅ Loaded ${changes.length} client changes from DB:`, changes);
        console.log(`📊 Raw DB data sample:`, changes[0] ? {
          id: changes[0].id,
          change_text: changes[0].change_text?.substring(0, 30),
          timing_minutes: changes[0].timing_minutes,
          hasTimingMinutes: 'timing_minutes' in changes[0]
        } : 'no changes');
        
        const mappedChanges = changes.map(c => ({
          id: c.id,
          text: c.change_text, // Ensure full text is preserved
          change_text: c.change_text, // Also preserve original field name for compatibility
          points: c.points,
          timing: c.timing_minutes || 2 // Default to 2 minutes if not set
        }));
        
        // Log full text lengths to verify no truncation
        console.log(`[LOAD-SESSION-CHANGES] 📏 Text lengths:`, mappedChanges.map(c => ({
          id: c.id,
          textLength: c.text?.length || 0,
          textPreview: c.text?.substring(0, 50) + (c.text && c.text.length > 50 ? '...' : ''),
          fullText: c.text // Log full text to verify
        })));
        console.log(`🎤 Voice timing values loaded:`, mappedChanges.map(c => ({ timing: c.timing, text: c.text.substring(0, 30) })));
        console.log(`[LOAD-SESSION-CHANGES] ✅ Setting clientChanges state (${mappedChanges.length} changes)`);
        setClientChanges(mappedChanges);
      } else {
        console.log('⚠️ No client changes found');
        setClientChanges([]);
      }

      // Load bonus change
      const { data: bonus, error: bonusError } = await supabase
        .from('client_changes')
        .select('*')
        .eq('game_id', game.id)
        .eq('session_name', currentSession.sessionKey)
        .eq('is_bonus', true)
        .limit(1)
        .single();

      if (bonusError && bonusError.code !== 'PGRST116') {
        console.error('Error loading bonus change:', bonusError);
      } else if (bonus) {
        const bonusChangeData = {
          id: bonus.id,
          text: bonus.change_text,
          points: bonus.points,
          timing: bonus.timing_minutes || 10 // Default to 10 minutes if not set
        };
        console.log(`🎤 Bonus timing value loaded:`, { timing: bonusChangeData.timing, text: bonusChangeData.text.substring(0, 30) });
        setBonusChange(bonusChangeData);
      } else {
        setBonusChange(null);
      }
    } catch (error) {
      console.error('Error loading session changes:', error);
    }
  };

  // HOST ONLY: Generate and store voice clip when minute mark is crossed
  const generateAndStoreVoiceClip = async (change, changeIndex, sessionName) => {
    console.log(`[HOST] 🎬 Starting generation for change ${changeIndex}:`, {
      changeText: change.text || change.change_text || change,
      sessionName,
      gameId: game.id,
      changeObject: change,
      timestamp: new Date().toISOString()
    });
    
    // Check ElevenLabs API key
    if (!isConfigured()) {
      console.error(`[HOST] ❌ ElevenLabs API key not configured! Cannot generate voice.`);
      throw new Error('ElevenLabs API key not configured');
    }
    
    try {
      // Check if already generated (prevent duplicates during same game session)
      // But allow re-generation if game timer was reset (timer_started_at is null)
      const { data: existingClips, error: checkError } = await supabase
        .from('voice_clips')
        .select('id, audio_url')
        .eq('game_id', game.id)
        .eq('session_name', sessionName)
        .eq('change_index', changeIndex);

      if (checkError) {
        console.error('[HOST] Error checking existing clip:', checkError);
      }

      const existing = existingClips && existingClips.length > 0 ? existingClips[0] : null;

      // Only skip if clip exists AND timer is currently running
      // If timer was reset (null), allow re-generation
      if (existing && game?.timer_started_at) {
        console.log(`[HOST] ⏭️  Clip already exists for change ${changeIndex} and timer is running, using existing clip`);
        // Use existing clip and play it
        const existingText = change.text || change.change_text || (typeof change === 'string' ? change : '');
        if (existing.audio_url) {
          console.log(`[HOST] 🎵 Playing existing voice clip: ${existing.audio_url}`);
          playVoiceReading(existingText, false, existing.audio_url);
        }
        return;
      } else if (existing && !game?.timer_started_at) {
        // Timer was reset, delete old clip and allow re-generation
        console.log(`[HOST] 🔄 Timer was reset - deleting old clip for change ${changeIndex} to allow re-generation`);
        const { error: deleteError } = await supabase
          .from('voice_clips')
          .delete()
          .eq('game_id', game.id)
          .eq('session_name', sessionName)
          .eq('change_index', changeIndex);
        
        if (deleteError) {
          console.error('[HOST] Error deleting old clip:', deleteError);
        }
      }

      // Get text from change object (handle different formats)
      const changeText = change.text || change.change_text || (typeof change === 'string' ? change : '');
      if (!changeText) {
        console.error('[HOST] ❌ No text found in change object:', change);
        throw new Error('No text found in client change');
      }

      // Ensure we have the full text (no truncation)
      const fullText = changeText.trim();
      
      // CRITICAL: Log the FULL text to verify it's complete
      console.log(`[HOST] 🎙️  Calling ElevenLabs API for change ${changeIndex}:`, {
        textPreview: fullText.substring(0, 100) + (fullText.length > 100 ? '...' : ''),
        fullText: fullText, // Log the ENTIRE text
        textLength: fullText.length,
        characterCount: fullText.length,
        wordCount: fullText.split(/\s+/).length,
        last50Chars: fullText.substring(Math.max(0, fullText.length - 50)), // Show last 50 chars to verify it's complete
        changeObject: {
          hasText: !!change.text,
          hasChangeText: !!change.change_text,
          textLength: change.text?.length || 0,
          changeTextLength: change.change_text?.length || 0,
          fullChangeObject: change
        },
        timestamp: new Date().toISOString()
      });
      
      // Verify text is not empty or just whitespace
      if (!fullText || fullText.length === 0) {
        console.error('[HOST] ❌ Full text is empty after trim:', {
          originalChangeText: changeText,
          trimmedLength: fullText.length,
          changeObject: change
        });
        throw new Error('Text is empty after processing');
      }

      // Generate audio via ElevenLabs
      const startTime = Date.now();
      let audioBlob;
      try {
        // Use fullText (trimmed) to ensure no truncation
        // Log exactly what we're sending to ElevenLabs
        console.log(`[HOST] 📤 Sending to ElevenLabs API:`, {
          textLength: fullText.length,
          textPreview: fullText.substring(0, 200),
          textEnd: fullText.substring(Math.max(0, fullText.length - 100)),
          completeText: fullText // Log the complete text being sent
        });
        
        audioBlob = await generateSpeech(fullText);
        const generationTime = Date.now() - startTime;
        
        // Calculate estimated duration more accurately (MP3 at 128kbps is ~16KB/sec, but varies)
        const estimatedDurationSeconds = Math.round(audioBlob.size / 16000);
        
        console.log(`[HOST] ✅ Audio generated successfully`, {
          textLength: fullText.length,
          textSent: fullText, // Log the complete text that was sent
          audioSize: audioBlob.size,
          audioSizeKB: (audioBlob.size / 1024).toFixed(2),
          generationTimeMs: generationTime,
          estimatedDurationSeconds: estimatedDurationSeconds,
          actualDurationEstimate: `${Math.floor(estimatedDurationSeconds / 60)}:${String(estimatedDurationSeconds % 60).padStart(2, '0')}`,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error(`[HOST] ❌ ElevenLabs API error:`, {
          error: error.message,
          stack: error.stack,
          changeIndex,
          changeText: changeText.substring(0, 100)
        });
        throw error;
      }
      
      // Upload to Supabase Storage
      const fileName = `${game.id}/${sessionName}/change_${changeIndex}.mp3`;
      console.log(`[HOST] 📤 Uploading to storage: ${fileName}`);
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('voice-clips')
        .upload(fileName, audioBlob, {
          contentType: 'audio/mpeg',
          upsert: true
        });

      if (uploadError) {
        console.error('[HOST] ❌ Upload error:', uploadError);
        console.error('[HOST] Upload error details:', {
          message: uploadError.message,
          statusCode: uploadError.statusCode,
          error: uploadError.error
        });
        throw uploadError;
      }

      console.log(`[HOST] ✅ Upload successful:`, uploadData);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('voice-clips')
        .getPublicUrl(fileName);

      console.log(`[HOST] 🔗 Public URL: ${publicUrl}`);

      // Store in database (this triggers real-time for all clients)
      // Use fullText to ensure complete text is stored
      // First try to delete any existing clip, then insert (handles duplicates better than upsert)
      const { error: deleteExistingError } = await supabase
        .from('voice_clips')
        .delete()
        .eq('game_id', game.id)
        .eq('session_name', sessionName)
        .eq('change_index', changeIndex);
      
      if (deleteExistingError && deleteExistingError.code !== 'PGRST116') {
        console.warn('[HOST] Warning deleting existing clip (might not exist):', deleteExistingError);
      }
      
      // Now insert the new clip
      const { data: insertData, error: insertError } = await supabase
        .from('voice_clips')
        .insert({
          game_id: game.id,
          session_name: sessionName,
          change_index: changeIndex,
          change_text: fullText, // Store full text, not truncated version
          audio_url: publicUrl
        })
        .select()
        .single();

      if (insertError) {
        console.error('[HOST] ❌ Insert error:', insertError);
        console.error('[HOST] Insert error details:', {
          message: insertError.message,
          code: insertError.code,
          details: insertError.details,
          hint: insertError.hint
        });
        
        // If it's a duplicate key error, the clip already exists in the database
        // Just play the audio we generated - it's already uploaded to storage
        if (insertError.code === '23505' || insertError.message?.includes('duplicate key')) {
          console.log('[HOST] ⚠️ Duplicate key detected - clip already exists in database');
          console.log('[HOST] 🎵 Playing newly generated audio (already uploaded to storage)');
          // Play the audio we just generated - it's already uploaded and ready
          playVoiceReading(fullText, false, publicUrl);
          return; // Successfully handled - audio will play
        }
        
        // For other errors, still try to play the audio we generated
        // The audio was successfully generated and uploaded, so we can still use it
        console.log('[HOST] ⚠️ Database insert failed but audio was generated, playing anyway...');
        playVoiceReading(fullText, false, publicUrl);
        // Don't throw error - user experience is more important than database consistency
        return;
      }

      console.log(`[HOST] ✅✅ Voice clip stored successfully: ${publicUrl}`);
      console.log(`[HOST] 📊 Insert data:`, insertData);
      
      // Play immediately for the host (who triggered it)
      // Don't wait for real-time subscription - play immediately for instant feedback
      console.log(`[HOST] 🎵 Playing voice clip immediately for host: ${publicUrl}`);
      console.log(`[HOST] 🎵 Calling playVoiceReading with:`, {
        textLength: fullText.length,
        textPreview: fullText.substring(0, 50),
        audioUrl: publicUrl,
        isBonus: false
      });
      
      try {
        playVoiceReading(fullText, false, publicUrl);
        console.log(`[HOST] ✅ playVoiceReading called successfully`);
      } catch (playError) {
        console.error(`[HOST] ❌ Error calling playVoiceReading:`, {
          error: playError.message,
          stack: playError.stack,
          audioUrl: publicUrl,
          name: playError.name
        });
        // Still try to play via real-time subscription as fallback
      }
      
      // Real-time subscription will also trigger playback for all clients (including host)
      // But we play immediately above to ensure host gets instant feedback
      console.log(`[HOST] 🎵 Voice clip inserted - real-time subscription will also trigger playback for all clients`);
      
    } catch (error) {
      console.error('[HOST] ❌ Error generating voice clip:', error);
      console.error('[HOST] Error stack:', error.stack);
      // Re-throw error so button handler can catch it
      throw error;
    }
  };

  // Play voice reading for a change (now handles both generated and pre-generated URLs)
  const playVoiceReading = async (text, isBonus = false, audioUrl = null) => {
    console.log(`[VOICE-PLAY] 🎤 playVoiceReading called`, {
      isBonus,
      textPreview: text.substring(0, 50),
      textLength: text.length,
      audioUrl: audioUrl ? audioUrl.substring(0, 50) + '...' : null,
      isPlayingRef: isPlayingAudioRef.current,
      timestamp: new Date().toISOString()
    });
    
    if (!isConfigured()) {
      console.error('[VOICE-PLAY] ❌ ElevenLabs API key not configured. Voice reading disabled.');
      console.error('[VOICE-PLAY] Check REACT_APP_ELEVENLABS_API_KEY environment variable');
      return;
    }

    // MUTEX PATTERN: Check if already playing BEFORE doing anything
    // This prevents multiple simultaneous playback attempts
    if (isPlayingAudioRef.current) {
      console.log('[VOICE-PLAY] ⏸️ Audio already playing, skipping new playback request');
      return;
    }

    // Set playing flag IMMEDIATELY to lock out other calls
    // This is the mutex lock - don't clear until playback finishes
    isPlayingAudioRef.current = true;
    console.log('[VOICE-PLAY] 🔒 Mutex lock acquired - isPlayingAudioRef set to true');
    
    // Clear any existing audio state (but keep the mutex lock)
    console.log('[VOICE-PLAY] 🔄 Clearing previous audio state');
    setIsVoicePlaying(false);
    setCurrentAudioUrl(null);
    
    // Clear timeout if exists
    if (voiceTimeoutRef.current) {
      clearTimeout(voiceTimeoutRef.current);
      voiceTimeoutRef.current = null;
    }

    try {
      let finalAudioUrl = audioUrl;
      
      // If no pre-generated URL provided, generate it (fallback for backward compatibility)
      if (!finalAudioUrl) {
        console.log(`[VOICE-PLAY] 🔊 Generating speech for: "${text.substring(0, 100)}..."`);
        const startTime = Date.now();
        try {
          finalAudioUrl = await generateSpeechUrl(text);
          const generationTime = Date.now() - startTime;
          console.log(`[VOICE-PLAY] ✅ Speech generated`, {
            urlPreview: finalAudioUrl.substring(0, 50),
            generationTimeMs: generationTime,
            timestamp: new Date().toISOString()
          });
        } catch (error) {
          console.error('[VOICE-PLAY] ❌ Error generating speech:', error);
          throw error;
        }
      } else {
        console.log(`[VOICE-PLAY] 🔊 Using pre-generated audio URL: ${finalAudioUrl.substring(0, 50)}...`);
      }
      
      // Set state - VoiceWaveform will handle playback via WaveSurfer
      console.log('[VOICE-PLAY] 📤 Setting audio URL and playing state');
      setCurrentAudioUrl(finalAudioUrl);
      setIsVoicePlaying(true);
      console.log('[VOICE-PLAY] ✅ State set - VoiceWaveform should start playing');
      
      // Clear any existing timeout first
      if (voiceTimeoutRef.current) {
        clearTimeout(voiceTimeoutRef.current);
        voiceTimeoutRef.current = null;
      }
      
      // Calculate estimated audio duration based on text length
      // Average speaking rate is ~150 words per minute = 2.5 words per second
      // Add buffer for pauses, intro/outro, and safety margin
      const wordCount = text.split(/\s+/).length;
      const estimatedDurationSeconds = Math.ceil(wordCount / 2.5) + 10; // +10 seconds buffer
      const timeoutMs = Math.max(estimatedDurationSeconds * 1000, 20000); // Minimum 20 seconds
      
      console.log(`[VOICE-PLAY] ⏱️ Audio playback started`, {
        textLength: text.length,
        wordCount: wordCount,
        estimatedDurationSeconds: estimatedDurationSeconds,
        timeoutMs: timeoutMs,
        timeoutSeconds: timeoutMs / 1000
      });
      
      // Set a reasonable safety timeout based on estimated duration
      // This will reset state if VoiceWaveform's onFinish doesn't fire
      voiceTimeoutRef.current = setTimeout(() => {
        if (isPlayingAudioRef.current) {
          console.log('[VOICE-PLAY] ⏱️ Safety timeout triggered - resetting state');
          console.log('[VOICE-PLAY] ⏱️ WaveSurfer onFinish may have failed to fire');
          isPlayingAudioRef.current = false;
          setIsVoicePlaying(false);
          setCurrentAudioUrl(null);
          voiceTimeoutRef.current = null;
          if (finalAudioUrl && finalAudioUrl.startsWith('blob:')) {
            URL.revokeObjectURL(finalAudioUrl);
          }
        }
      }, timeoutMs);
      
      console.log('✅ Audio URL set - VoiceWaveform will handle playback, safety timeout set');
    } catch (error) {
      console.error('❌ Error generating speech:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        code: error.code,
        stack: error.stack
      });
      // Clear voice timeout if exists
      if (voiceTimeoutRef.current) {
        clearTimeout(voiceTimeoutRef.current);
        voiceTimeoutRef.current = null;
      }
      isPlayingAudioRef.current = false;
      setIsVoicePlaying(false);
      setCurrentAudioUrl(null);
    }
  };

  // Check if it's time to read a change based on timing config
  const checkVoiceTiming = () => {
    // Debug logging
    if (!voiceTimingConfig) {
      console.log('🔇 Voice timing: No config loaded');
      return;
    }
    if (!timerStarted) {
      console.log('🔇 Voice timing: Timer not started');
      return;
    }
    if (timeRemaining === null) {
      console.log('🔇 Voice timing: Time remaining is null');
      return;
    }
    if (clientChanges.length === 0) {
      console.log('🔇 Voice timing: No client changes loaded');
      return;
    }

    const currentSession = sessions[currentSessionIndex];
    if (!currentSession) {
      console.log('🔇 Voice timing: No current session');
      return;
    }

    const totalDuration = (currentSession.timerDuration || 15) * 60; // Convert to seconds
    const elapsed = totalDuration - timeRemaining;
    const elapsedPercentage = (elapsed / totalDuration) * 100;
    
    console.log('🔊 Voice timing check:', {
      mode: voiceTimingConfig.mode,
      values: voiceTimingConfig.values,
      elapsed: elapsed,
      elapsedPercentage: elapsedPercentage.toFixed(1),
      timeRemaining: timeRemaining,
      totalDuration: totalDuration,
      clientChangesCount: clientChanges.length,
      readChanges: Array.from(readChanges)
    });

    // Check client changes timing
    if (voiceTimingConfig.mode === 'percentage') {
      voiceTimingConfig.values.forEach((percentage, index) => {
        const threshold = (percentage / 100) * totalDuration;
        const changeIndex = index;
        
        console.log(`  Checking change ${changeIndex}: threshold=${threshold.toFixed(1)}s, elapsed=${elapsed.toFixed(1)}s, inRange=${elapsed >= threshold && elapsed < threshold + 5}, alreadyRead=${readChanges.has(`change_${changeIndex}`)}`);
        
        if (elapsed >= threshold && elapsed < threshold + 5 && !readChanges.has(`change_${changeIndex}`)) {
          if (clientChanges[changeIndex]) {
            console.log(`✅ Triggering voice reading for change ${changeIndex}: "${clientChanges[changeIndex].text.substring(0, 50)}..."`);
            setReadChanges(prev => new Set([...prev, `change_${changeIndex}`]));
            playVoiceReading(clientChanges[changeIndex].text, false);
          }
        }
      });

      // Check bonus timing
      if (bonusChange && elapsedPercentage >= voiceTimingConfig.bonusValue && elapsedPercentage < voiceTimingConfig.bonusValue + 1) {
        if (!readChanges.has('bonus')) {
          setReadChanges(prev => new Set([...prev, 'bonus']));
          playVoiceReading(bonusChange.text, true);
        }
      }
    } else {
      // Specific times mode
      voiceTimingConfig.values.forEach((minutes, index) => {
        const threshold = minutes * 60; // Convert to seconds
        const changeIndex = index;
        
        console.log(`  Checking change ${changeIndex}: threshold=${threshold}s (${minutes}min), elapsed=${elapsed.toFixed(1)}s, inRange=${elapsed >= threshold && elapsed < threshold + 5}, alreadyRead=${readChanges.has(`change_${changeIndex}`)}`);
        
        if (elapsed >= threshold && elapsed < threshold + 5 && !readChanges.has(`change_${changeIndex}`)) {
          if (clientChanges[changeIndex]) {
            console.log(`✅ Triggering voice reading for change ${changeIndex}: "${clientChanges[changeIndex].text.substring(0, 50)}..."`);
            setReadChanges(prev => new Set([...prev, `change_${changeIndex}`]));
            playVoiceReading(clientChanges[changeIndex].text, false);
          }
        }
      });

      // Check bonus timing
      const bonusThreshold = voiceTimingConfig.bonusValue * 60;
      if (bonusChange && elapsed >= bonusThreshold && elapsed < bonusThreshold + 5) {
        if (!readChanges.has('bonus')) {
          setReadChanges(prev => new Set([...prev, 'bonus']));
          playVoiceReading(bonusChange.text, true);
        }
      }
    }
  };

  // NOTE: Automatic voice scheduling removed - using manual button triggers instead
  // Voice clips are now triggered manually by the host clicking "Read Aloud" buttons

  // ALL CLIENTS: Subscribe to voice clips and play them
  useEffect(() => {
    if (!game?.id || sessions.length === 0) {
      console.log('[CLIENT] ⚠️ Cannot subscribe to voice clips:', { gameId: game?.id, sessionsLength: sessions.length });
      return;
    }

    const currentSession = sessions[currentSessionIndex];
    if (!currentSession) {
      console.log('[CLIENT] ⚠️ No current session, cannot subscribe to voice clips');
      return;
    }

    console.log('[CLIENT] 🎧 Subscribing to voice clips...', {
      gameId: game.id,
      currentSessionIndex,
      currentSessionName: currentSession.session_name || currentSession.sessionKey,
      isHost: isHost()
    });

    const channel = supabase
      .channel(`voice-clips-${game.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'voice_clips',
          filter: `game_id=eq.${game.id}`
        },
        (payload) => {
          console.log('[CLIENT] 🔔🔔🔔 VOICE CLIP INSERT EVENT RECEIVED! 🔔🔔🔔');
          console.log('[CLIENT] Full payload:', JSON.stringify(payload, null, 2));
          
          const newClip = payload.new;
          const currentSessionAtTime = sessions[currentSessionIndex];
          const sessionMatch = newClip.session_name === (currentSessionAtTime?.session_name || currentSessionAtTime?.sessionKey);
          
          console.log('[CLIENT] New voice clip received:', {
            clipSessionName: newClip.session_name,
            currentSessionName: currentSessionAtTime?.session_name,
            currentSessionKey: currentSessionAtTime?.sessionKey,
            sessionMatch,
            changeIndex: newClip.change_index,
            audioUrl: newClip.audio_url?.substring(0, 50),
            fullAudioUrl: newClip.audio_url
          });
          
          // Only play if it's for current session
          if (sessionMatch) {
            console.log('[CLIENT] ✅ Session matches! Preparing to play...');
            // DEBOUNCING: Use setTimeout to debounce rapid subscription events
            // This prevents multiple rapid calls if subscription fires multiple times
            const clipId = `${newClip.game_id}_${newClip.session_name}_${newClip.change_index}`;
            
            // Clear any existing timeout for this clip
            if (channel._debounceTimeouts && channel._debounceTimeouts[clipId]) {
              clearTimeout(channel._debounceTimeouts[clipId]);
            }
            
            // Set up debounce timeout
            if (!channel._debounceTimeouts) {
              channel._debounceTimeouts = {};
            }
            
            channel._debounceTimeouts[clipId] = setTimeout(() => {
              console.log('[CLIENT] ✅ Session matches, playing voice clip (debounced)');
              console.log('[CLIENT] 🎵 Calling playVoiceClipFromUrl with clip:', {
                game_id: newClip.game_id,
                session_name: newClip.session_name,
                change_index: newClip.change_index,
                audio_url: newClip.audio_url
              });
            playVoiceClipFromUrl(newClip);
              delete channel._debounceTimeouts[clipId];
            }, 100); // 100ms debounce - enough to catch rapid duplicates, fast enough for UX
          } else {
            console.log('[CLIENT] ⏸️ Session mismatch, skipping voice clip');
            console.log('[CLIENT] Session mismatch details:', {
              clipSession: newClip.session_name,
              currentSession: currentSessionAtTime?.session_name || currentSessionAtTime?.sessionKey,
              currentSessionIndex
            });
          }
        }
      )
      .subscribe((status) => {
        console.log('[CLIENT] 🎧 Voice clips subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('[CLIENT] ✅ Successfully subscribed to voice clips channel!');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[CLIENT] ❌ Voice clips channel subscription error!');
        } else if (status === 'TIMED_OUT') {
          console.error('[CLIENT] ❌ Voice clips channel subscription timed out!');
        } else {
          console.log('[CLIENT] ⚠️ Voice clips subscription status:', status);
        }
      });

    return () => {
      console.log('[CLIENT] Unsubscribing from voice clips');
      // Clear any pending debounce timeouts
      if (channel._debounceTimeouts) {
        Object.values(channel._debounceTimeouts).forEach(timeout => clearTimeout(timeout));
        channel._debounceTimeouts = {};
      }
      supabase.removeChannel(channel);
    };
  }, [game?.id, currentSessionIndex, sessions]);

  // Play voice clip from pre-generated URL
  const playVoiceClipFromUrl = (clip) => {
    console.log('[CLIENT] 🎵 playVoiceClipFromUrl called with clip:', {
      game_id: clip.game_id,
      session_name: clip.session_name,
      change_index: clip.change_index,
      audio_url: clip.audio_url,
      change_text: clip.change_text?.substring(0, 50)
    });
    
    // STRONG DEDUPLICATION: Check mutex FIRST before any other checks
    if (isPlayingAudioRef.current) {
      console.log('[CLIENT] ⏸️ Mutex locked - audio already playing, skipping duplicate playback');
      return;
    }

    // Check if we've already played this exact clip (deduplication)
    const clipId = `${clip.game_id}_${clip.session_name}_${clip.change_index}`;
    if (playedVoiceIdsRef.current.has(clipId)) {
      console.log('[CLIENT] ⏸️ This voice clip was already played, skipping:', clipId);
      return;
    }

    // Mark as played BEFORE calling playVoiceReading
    // This ensures deduplication works even if multiple subscription events fire rapidly
    playedVoiceIdsRef.current.add(clipId);
    console.log('[CLIENT] 🎵 Playing voice clip:', clip.audio_url);
    console.log('[CLIENT] 🎵 Calling playVoiceReading with:', {
      text: clip.change_text?.substring(0, 50),
      isBonus: false,
      audioUrl: clip.audio_url
    });
    playVoiceReading(clip.change_text, false, clip.audio_url);
  };

  const startTimer = () => {
    console.log('startTimer called', { sessionsLength: sessions.length, currentSessionIndex, timerStarted });
    
    if (sessions.length === 0) {
      console.log('Cannot start timer: no sessions loaded');
      return;
    }
    
    if (currentSessionIndex >= sessions.length) {
      console.log('Cannot start timer: session index out of bounds');
      return;
    }
    
    const currentSession = sessions[currentSessionIndex];
    if (!currentSession) {
      console.log('Cannot start timer: current session is null');
      return;
    }
    
    console.log('Current session:', currentSession);
    const durationInMinutes = currentSession.timerDuration || 15;
    const durationInSeconds = durationInMinutes * 60;
    
    console.log(`Starting timer: ${durationInMinutes} minutes (${durationInSeconds} seconds)`);
    
    // Always use database for timer synchronization - no localStorage fallback
    // This ensures all clients stay perfectly synchronized
    supabase
      .from('games')
      .select('timer_started_at, timer_duration_seconds, current_session_index')
      .eq('id', game.id)
      .single()
      .then(({ data, error }) => {
        if (!error && data?.timer_started_at && data?.timer_duration_seconds) {
          // Use database timer for synchronization
          const timerStartTime = new Date(data.timer_started_at);
          const now = new Date();
          const elapsed = Math.floor((now - timerStartTime) / 1000);
          const remaining = Math.max(0, data.timer_duration_seconds - elapsed);
          
          console.log(`Using database timer: ${remaining} seconds remaining`);
        setTimeRemaining(remaining);
        setTimerStarted(true);
          
          // Update session index if it changed
          if (data.current_session_index !== undefined && 
              data.current_session_index !== currentSessionIndex) {
            setCurrentSessionIndex(data.current_session_index);
          }
        
        if (remaining === 0) {
          handleTimerEnd();
      }
    } else {
          // If no database timer exists, this means timer hasn't been started yet
          // Only set initial state, don't start the timer
          console.log('No database timer found - timer not started yet');
      setTimeRemaining(durationInSeconds);
          setTimerStarted(false);
        }
      });
  };

  const handleTimerEnd = async () => {
    const currentSession = sessions[currentSessionIndex];
    if (!currentSession) return;
    
    // Mark current session as complete
    // Even if timer_started_at is null (e.g., after game status changes to finished),
    // we should still mark the session as complete if the timer has ended
    console.log('✅ Marking session as complete:', currentSession.sessionKey);
    setCompletedSessions(prev => new Set([...prev, currentSession.sessionKey]));
    
    // Stop the timer - don't automatically move to next session
      setTimerStarted(false);
      setTimeRemaining(null);
    
    // Check if all sessions are complete
    const allComplete = completedSessions.size + 1 >= sessions.length; // +1 for current session
    if (allComplete) {
      setAllSessionsComplete(true);
      // Update game status to finished
      const { error } = await supabase
        .from('games')
        .update({ game_status: 'finished' })
        .eq('id', game.id);
      
      if (error) {
        console.error('Error updating game status:', error);
      }
    }
  };

  const handleStartSession = async (sessionIndex) => {
    try {
      const newSession = sessions[sessionIndex];
      if (!newSession) {
        console.error('Cannot start session: session not found');
        return;
      }

      // Clear any existing voice clips for this game session
      const sessionName = newSession.session_name || newSession.sessionKey || `session${sessionIndex === 0 ? 'A' : 'B'}`;
      console.log('[START-SESSION] Clearing old voice clips for session:', sessionName);
      
      const { error: deleteError } = await supabase
        .from('voice_clips')
        .delete()
        .eq('game_id', game.id)
        .eq('session_name', sessionName);
      
      if (deleteError) {
        console.error('[START-SESSION] ⚠️ Error clearing voice clips:', deleteError);
        // Don't fail the session start if voice clip deletion fails
      } else {
        console.log('[START-SESSION] ✅ Old voice clips cleared for session:', sessionName);
      }
      
      // Clear the played voice IDs ref to reset deduplication for new session
      playedVoiceIdsRef.current.clear();
      console.log('[START-SESSION] ✅ Played voice IDs cleared');

      // Calculate timer duration
      const durationInMinutes = newSession.timerDuration || 15;
      const durationInSeconds = durationInMinutes * 60;
      
      // Store timer start time in database for real-time synchronization
      const timerStartTime = new Date().toISOString();
      
      // Update database with new session timer start
      const { error } = await supabase
        .from('games')
        .update({ 
          timer_started_at: timerStartTime,
          current_session_index: sessionIndex,
          timer_duration_seconds: durationInSeconds
        })
        .eq('id', game.id);
      
      if (error) {
        console.error('Error starting session:', error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        
        // Check if it's a column missing error
        if (error.message && (
          error.message.includes('column') && (
            error.message.includes('timer_started_at') ||
            error.message.includes('current_session_index') ||
            error.message.includes('timer_duration_seconds')
          )
        )) {
          alert('Error: Timer synchronization columns are missing. Please run the migration file add_timer_sync_columns.sql in your Supabase SQL editor.');
          return;
        }
        
        alert('Failed to start session. Please try again.');
        return;
      }

      // Set the current session index
      setCurrentSessionIndex(sessionIndex);
      setTimerStarted(false);
      setTimeRemaining(null);
      
      // Load session changes for the new session
      // Small delay to ensure state is updated
      setTimeout(async () => {
        // Reset read changes for the new session
        setReadChanges(new Set());
        // Start the timer
        startTimer();
      }, 100);
    } catch (error) {
      console.error('Error starting session:', error);
      alert('Failed to start session. Please try again.');
    }
  };

  const handleStartGame = async (e) => {
    // CRITICAL: Prevent any default behavior (form submission, navigation, etc.)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('[START-GAME] === START GAME CLICKED ===');
    console.log('[START-GAME] Event:', e ? 'Event received' : 'No event');
    console.log('[START-GAME] Game ID:', game?.id);
    console.log('[START-GAME] Current status:', localGameStatus);
    console.log('[START-GAME] Is host:', isHost());
    console.log('[START-GAME] Timer started:', timerStarted);
    console.log('[START-GAME] Current session index:', currentSessionIndex);
    console.log('[START-GAME] Sessions count:', sessions.length);
    
    // Ensure game is in 'published' status (or 'started' for safety)
    if (localGameStatus !== 'published' && localGameStatus !== 'started') {
      console.log('[START-GAME] ⚠️ Game is not in published/started status, ignoring click');
      return;
    }
    
    // Prevent multiple simultaneous calls
    if (timerStarted || isStartingGame) {
      console.log('[START-GAME] ⚠️ Already starting or started, ignoring click');
      return;
    }
    
    // Set flag to prevent status check from triggering reload
    setIsStartingGame(true);
    console.log('[START-GAME] Set isStartingGame flag to prevent status check reload');
    
    // Update local status IMMEDIATELY to prevent status check from triggering
    setLocalGameStatus('started');
    console.log('[START-GAME] Updated localGameStatus to "started" immediately');
    
    try {
      const currentSession = sessions[currentSessionIndex];
      if (!currentSession) {
        console.error('[START-GAME] ❌ Cannot start game: no current session');
        return;
      }

      console.log('[START-GAME] Current session:', {
        name: currentSession.name,
        sessionKey: currentSession.sessionKey || currentSession.session_name,
        timerDuration: currentSession.timerDuration
      });

      // Clear any existing voice clips for this game session
      const sessionName = currentSession.session_name || currentSession.sessionKey || `session${currentSessionIndex === 0 ? 'A' : 'B'}`;
      console.log('[START-GAME] Clearing old voice clips for session:', sessionName);
      
      const { error: deleteError } = await supabase
        .from('voice_clips')
        .delete()
        .eq('game_id', game.id)
        .eq('session_name', sessionName);
      
      if (deleteError) {
        console.error('[START-GAME] ⚠️ Error clearing voice clips:', deleteError);
        // Don't fail the game start if voice clip deletion fails
      } else {
        console.log('[START-GAME] ✅ Old voice clips cleared for session:', sessionName);
      }
      
      // Clear the played voice IDs ref to reset deduplication
      playedVoiceIdsRef.current.clear();
      console.log('[START-GAME] ✅ Played voice IDs cleared');

      // Calculate timer duration
      const durationInMinutes = currentSession.timerDuration || 15;
      const durationInSeconds = durationInMinutes * 60;
      
      console.log('[START-GAME] Timer duration:', durationInMinutes, 'minutes =', durationInSeconds, 'seconds');
      
      // Store timer start time in database for real-time synchronization
      const timerStartTime = new Date().toISOString();
      console.log('[START-GAME] Timer start time:', timerStartTime);
      
      // First, try to update with timer sync columns
      let updateData = { 
        game_status: 'started',
        timer_started_at: timerStartTime,
        current_session_index: currentSessionIndex,
        timer_duration_seconds: durationInSeconds
      };
      
      console.log('[START-GAME] Updating database with:', updateData);
      
      const { error, data } = await supabase
        .from('games')
        .update(updateData)
        .eq('id', game.id)
        .select();
      
      if (error) {
        console.error('[START-GAME] ❌ Error starting game:', error);
        console.error('[START-GAME] Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        
        // Check if it's a column missing error
        if (error.message && (
          error.message.includes('column') && (
            error.message.includes('timer_started_at') ||
            error.message.includes('current_session_index') ||
            error.message.includes('timer_duration_seconds')
          )
        )) {
          alert('Error: Timer synchronization columns are missing. Please run the migration file add_timer_sync_columns.sql in your Supabase SQL editor.');
        } else {
          // Fallback: try updating without timer columns (for backward compatibility)
          console.log('[START-GAME] Attempting fallback update without timer columns...');
          const { error: fallbackError } = await supabase
            .from('games')
            .update({ game_status: 'started' })
            .eq('id', game.id);
          
          if (fallbackError) {
            console.error('[START-GAME] ❌ Fallback update also failed:', fallbackError);
            alert('Failed to start game. Please try again.');
          } else {
            console.log('[START-GAME] ✅ Fallback update succeeded');
            // Start timer locally even if DB sync fails
            if (sessions.length > 0 && currentSessionIndex < sessions.length && !timerStarted) {
              console.log('[START-GAME] Starting timer locally...');
              startTimer();
            }
          }
        }
      } else {
        console.log('[START-GAME] ✅ Database update succeeded');
        console.log('[START-GAME] Updated game data:', data);
        // Success - start timer locally for immediate UI update
        if (sessions.length > 0 && currentSessionIndex < sessions.length && !timerStarted) {
          console.log('[START-GAME] Starting timer locally...');
          startTimer();
          console.log('[START-GAME] ✅✅ Game started successfully - timer should be running now');
        } else {
          console.warn('[START-GAME] ⚠️ Conditions not met for starting timer:', {
            hasSessions: sessions.length > 0,
            validIndex: currentSessionIndex < sessions.length,
            timerNotStarted: !timerStarted
          });
        }
      }
    } catch (error) {
      console.error('[START-GAME] ❌ Exception caught:', error);
      console.error('[START-GAME] Error stack:', error.stack);
      // Revert local status on error
      setLocalGameStatus('draft');
      alert('Failed to start game. Please try again.');
    } finally {
      // Clear flag after a short delay to allow state to settle
      setTimeout(() => {
        setIsStartingGame(false);
        console.log('[START-GAME] Cleared isStartingGame flag');
      }, 1000);
    }
  };

  // Reset timer state when moving to a new session
  useEffect(() => {
    if (sessions.length > 0 && currentSessionIndex < sessions.length && (game?.game_status === 'started' || game?.game_status === 'published')) {
      // Reset timer state - the main useEffect will start the new timer
      hasStartedIntervalRef.current = false; // Reset flag for new session
      setTimerStarted(false);
      setTimeRemaining(null);
    }
  }, [currentSessionIndex, sessions.length, game?.game_status]);

  const formatTime = (seconds, session) => {
    if (seconds === null || seconds === undefined || seconds < 0) {
      // If timer hasn't started yet, show the initial duration
      if (session && session.timerDuration) {
        const mins = session.timerDuration;
        return `${String(mins).padStart(2, '0')}:00`;
      }
      return '00:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Generate avatar URL from initials or use uploaded image
  const getAvatarUrl = (designer) => {
    // If designer is an object with avatarUrl, use it
    if (typeof designer === 'object' && designer.avatarUrl) {
      return designer.avatarUrl;
    }
    // Return null if no avatar (will show initials instead)
    return null;
  };

  // Helper function to get designer initials
  const getDesignerInitials = (designer) => {
    const name = typeof designer === 'string' ? designer : designer.name;
    if (!name) return '??';
    const initials = name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    return initials || '??';
  };

  const handleDesignerClick = (designerName) => {
    // Show designer details or handle interaction
    console.log('Designer clicked:', designerName);
    // TODO: Implement designer details modal or action
  };

  const handleRemoveDesigner = async (designerName) => {
    if (!window.confirm(`Remove ${designerName} from this game?`)) {
      return;
    }

    try {
      const updatedDesigners = designers.filter(d => {
        const name = typeof d === 'string' ? d : d.name;
        return name !== designerName;
      });
      const { error } = await supabase
        .from('games')
        .update({ designers: updatedDesigners })
        .eq('id', game.id);

      if (error) {
        throw error;
      }

      setDesigners(updatedDesigners);
    } catch (error) {
      console.error('Error removing designer:', error);
      alert('Failed to remove designer. Please try again.');
    }
  };

  const handleDesignerAvatarUpload = async (designerIndex, file) => {
    if (!file) return;

    try {
      const designer = designers[designerIndex];
      const designerName = typeof designer === 'string' ? designer : designer.name;
      
      // Upload avatar
      const { url } = await uploadDesignerAvatar(file, designerName, game.id);
      
      // Update designer in array, preserving all existing properties
      const updatedDesigners = [...designers];
      const currentDesigner = typeof designer === 'string' 
        ? { name: designer, avatarUrl: null }
        : designer;
      
      updatedDesigners[designerIndex] = {
        ...currentDesigner, // Preserve all existing properties (email, userId, isParticipant, etc.)
        name: designerName,
        avatarUrl: url
      };
      
      // Update database
      const { error } = await supabase
        .from('games')
        .update({ designers: updatedDesigners })
        .eq('id', game.id);

      if (error) {
        throw error;
      }

      setDesigners(updatedDesigners);
    } catch (error) {
      console.error('Error uploading designer avatar:', error);
      alert('Failed to upload avatar. Please try again.');
    }
  };

  const handleAddDesigner = async () => {
    if (!newDesignerName.trim()) {
      alert('Please enter a designer name');
      return;
    }

    const designerName = newDesignerName.trim();
    const isDuplicate = designers.some(d => {
      const name = typeof d === 'string' ? d : d.name;
      return name.toLowerCase() === designerName.toLowerCase();
    });

    if (isDuplicate) {
      alert('This designer is already added');
      return;
    }

    setAddingDesigner(true);
    try {
      const updatedDesigners = [...designers, { name: designerName, avatarUrl: null }];
      
      const { error } = await supabase
        .from('games')
        .update({ designers: updatedDesigners })
        .eq('id', game.id);

      if (error) {
        throw error;
      }

      setDesigners(updatedDesigners);
      setNewDesignerName('');
    } catch (error) {
      console.error('Error adding designer:', error);
      alert('Failed to add designer. Please try again.');
    } finally {
      setAddingDesigner(false);
    }
  };

  // If exiting, don't render anything - navigation is in progress
  if (isExiting) {
    console.log('[RENDER] Component is exiting - showing exit message instead of full UI');
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#060010',
        color: '#ffffff',
        gap: '1rem'
      }}>
        <p style={{ fontSize: '1.2rem' }}>Exiting game...</p>
        <p style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
          Returning to game management...
        </p>
      </div>
    );
  }

  const currentSession = sessions[currentSessionIndex] || null;

  // Show loading while checking access
  if (checkingAccess) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#060010',
        color: '#ffffff'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Show access denied if user doesn't have access
  if (!hasAccess) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#060010',
        color: '#ffffff',
        padding: '2rem'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '2rem',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <h2 style={{ marginTop: 0 }}>Access Denied</h2>
          <p>You don't have access to this game.</p>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>
            Please join the game using the join code provided by the host.
          </p>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                marginTop: '1.5rem',
                padding: '0.75rem 2rem',
                background: '#D702F6',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#C001E5';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#D702F6';
              }}
            >
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>

      <CardNav
        logo="/rollerCoaster_logo.svg"
        logoAlt="Client Roller Coaster Logo"
        logoText={game?.game_name || 'Active Game'}
        baseColor="#242323"
        menuColor="#fff"
        showBackButton={true}
        backButtonText="Exit"
        onBackClick={handleExit}
        showHamburger={false}
        showButton={false}
        items={[]}
      />
      <main 
        className="app-main" 
        data-view="active-game"
        style={{ 
          paddingTop: 'calc(2em + 60px + 2rem)',
          backgroundColor: '#060010',
          minHeight: '100vh'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2rem' }}>
          {/* Designers and Join Code Side by Side */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: (isHost() && (localGameStatus === 'published' || game?.game_status === 'published')) ? '1fr 1fr' : '1fr',
            gap: '2rem',
            marginBottom: '2rem'
          }}>
            {/* Designers Section - Left Side */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: '#ffffff', margin: 0, fontSize: '1.25rem' }}>
                {(() => {
                  // Helper function to filter active designers
                  const getActiveDesigners = () => {
                    return designers.filter(designer => {
                      const designerUserId = designer?.userId;
                      const designerEmail = designer?.email?.toLowerCase()?.trim();
                      const isHostDesigner = game?.created_by === designerUserId;
                      
                      // Don't show the host (game creator)
                      if (isHostDesigner) return false;
                      
                      // Check if player is online via presence
                      const hasUserIdMatch = designerUserId && activePlayerIds.has(designerUserId);
                      const hasEmailMatch = designerEmail && activePlayerEmails.has(designerEmail);
                      const isOnline = hasUserIdMatch || hasEmailMatch;
                      
                      // Debug logging to see why matching fails
                      if (!isOnline) {
                        console.log('[DESIGNERS-FILTER] ❌ Designer not online:', {
                          name: designer?.name,
                          userId: designerUserId,
                          email: designerEmail,
                          activePlayerIds: Array.from(activePlayerIds),
                          activePlayerEmails: Array.from(activePlayerEmails),
                          hasUserIdMatch,
                          hasEmailMatch
                        });
                      }
                      
                      return isOnline;
                    });
                  };
                  
                  const activeDesigners = getActiveDesigners();
                  
                  // Debug logging
                  console.log('[DESIGNERS-FILTER] Filtering designers:', {
                    totalDesigners: designers.length,
                    activeDesigners: activeDesigners.length,
                    activePlayerIds: Array.from(activePlayerIds),
                    activePlayerEmails: Array.from(activePlayerEmails),
                    designers: designers.map(d => ({
                      name: typeof d === 'string' ? d : d.name,
                      userId: d?.userId,
                      email: d?.email,
                      isParticipant: d?.isParticipant
                    }))
                  });
                  
                  if (isHost()) {
                    return `Designers (${activeDesigners.length})`;
                  } else {
                    return `Who's Playing (${activeDesigners.length})`;
                  }
                })()}
              </h3>
            </div>

            {/* Designers List - Chips Display */}
            {designers.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '2rem', 
                color: 'rgba(255, 255, 255, 0.7)' 
              }}>
                No players have joined
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {designers
                  .filter(designer => {
                    const designerUserId = designer?.userId;
                    const designerEmail = designer?.email?.toLowerCase()?.trim();
                    const isHostDesigner = game?.created_by === designerUserId;
                    
                    // Don't show the host (game creator)
                    if (isHostDesigner) return false;
                    
                    // Only show players who are currently online (in presence sets)
                    const isOnline = (designerUserId && activePlayerIds.has(designerUserId)) ||
                                    (designerEmail && activePlayerEmails.has(designerEmail));
                    
                    return isOnline;
                  })
                  .map((designer, index) => {
                  // Use full name from designer object, or construct from firstName/lastName if available
                  let designerName = typeof designer === 'string' ? designer : designer.name;
                  // If designer has firstName/lastName but name is missing or incomplete, construct it
                  if (typeof designer === 'object' && designer.firstName && (!designerName || designerName.trim() === designer.firstName)) {
                    const fullName = `${designer.firstName || ''} ${designer.lastName || ''}`.trim();
                    if (fullName) designerName = fullName;
                  }
                  const avatarUrl = getAvatarUrl(designer);
                  const initials = getDesignerInitials(designer);
                  // Check if this designer has joined (is a participant)
                  // Hosts are always considered "joined" (they created the game)
                  const designerUserId = designer?.userId;
                  const isHostDesigner = game?.created_by === designerUserId;
                  const isCurrentUser = user?.id && designerUserId === user.id;
                  const isEmailOnlyParticipant = designer?.isParticipant === true && !designerUserId;
                  const hasJoined = isHostDesigner || // Host is always considered joined
                                   (designerUserId && participantUserIds.has(designerUserId)) ||
                                   isEmailOnlyParticipant || // Email-only participants have joined
                                   // If current user is viewing themselves and has joined, show as joined
                                   (isCurrentUser && participantUserIds.has(user.id));
                  
                  // Debug logging
                  if (process.env.NODE_ENV === 'development') {
                    console.log(`[DESIGNER-STATUS] ${designerName}:`, {
                      designerUserId,
                      isHostDesigner,
                      isCurrentUser,
                      userInParticipants: user?.id && participantUserIds.has(user.id),
                      designerInParticipants: designerUserId && participantUserIds.has(designerUserId),
                      hasJoined,
                      participantUserIds: Array.from(participantUserIds)
                    });
                  }
                  
                  return (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        background: hasJoined 
                          ? 'rgba(76, 175, 80, 0.2)' 
                          : 'rgba(215, 2, 246, 0.2)',
                        color: hasJoined ? '#4CAF50' : '#D702F6',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        border: hasJoined 
                          ? '1px solid rgba(76, 175, 80, 0.3)' 
                          : 'none'
                      }}
                    >
                      {/* Avatar */}
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            border: hasJoined 
                              ? '2px solid #4CAF50' 
                              : '2px solid rgba(215, 2, 246, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            background: avatarUrl 
                              ? `url(${avatarUrl}) center/cover`
                              : hasJoined
                                ? 'rgba(76, 175, 80, 0.3)'
                              : 'rgba(215, 2, 246, 0.3)',
                            color: '#ffffff',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        >
                          {!avatarUrl && initials}
                        </div>
                      </div>
                      {/* Name */}
                      <span>{designerName}</span>
                      {/* Checkmark */}
                      {hasJoined && (
                        <span style={{ 
                          color: '#4CAF50', 
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          marginLeft: '0.25rem'
                        }}>✓</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            </div>

            {/* Join Code Display - Right Side - Host Only */}
            {isHost() && (localGameStatus === 'published' || game?.game_status === 'published') && (
              <div style={{
                background: 'linear-gradient(90deg, #D702F6 0%, #7B008D 100%)',
                borderRadius: '12px',
                padding: '2rem',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(215, 2, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: '100%'
              }}>
                <h3 style={{ color: 'rgba(255, 255, 255, 0.9)', margin: '0 0 1rem 0', fontSize: '1.25rem' }}>
                  Join Code
                </h3>
                {joinCode ? (
                  <>
                    <div 
                      style={{ 
                        fontSize: '3rem', 
                        fontWeight: 800, 
                        letterSpacing: '0.2rem',
                        fontFamily: 'monospace',
                        color: '#ffffff',
                        textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                        cursor: 'pointer',
                        userSelect: 'all'
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(joinCode);
                        alert('Join code copied to clipboard!');
                      }}
                      title="Click to copy"
                    >
                      {joinCode}
                    </div>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: '1rem 0 0 0', fontSize: '0.9rem' }}>
                      Share this code with designers to let them join
                    </p>
                  </>
                ) : (
                  <div style={{ color: '#fff' }}>
                    Loading join code...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Timer Cards Section - Show all sessions */}
          {/* Only show for hosts or when game is published/started OR timer has started */}
          {sessions.length > 0 && (isHost() || localGameStatus === 'published' || localGameStatus === 'started' || game?.timer_started_at) && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: sessions.length === 1 ? '1fr' : '1fr 1fr',
              gap: '2rem',
              marginBottom: '2rem'
            }}>
              {sessions.map((session, index) => {
                const isActive = index === currentSessionIndex && timerStarted;
                // Mark as completed if:
                // 1. Session is in completedSessions Set (timer ended)
                // 2. OR game status is 'finished' and this is the current session (timer definitely ended)
                // 3. OR timer has ended (timeRemaining === 0) and timer was started
                // IMPORTANT: When game status is 'finished', we know the timer ended, so mark current session as completed
                const sessionWasStarted = game?.timer_started_at || timerStarted || localGameStatus === 'finished';
                const timerEnded = timeRemaining === 0 && sessionWasStarted;
                const isCompleted = completedSessions.has(session.sessionKey) || 
                                   (localGameStatus === 'finished' && index === currentSessionIndex) ||
                                   (timerEnded && index === currentSessionIndex);
                const isLocked = !isActive && !isCompleted && index > currentSessionIndex;
                
                // Show "Start Game" button for first session when game is published but timer hasn't started
                // Only show for hosts, and NOT if the session is already completed
                const showStartGameButton = index === 0 && (localGameStatus === 'published' || game?.game_status === 'published') && !timerStarted && !isCompleted && isHost();
                
                // Show "Start Session" button for sessions that are ready to start:
                // - Next session after a completed one
                // Only show for hosts
                const isNextInSequence = index > 0 && completedSessions.has(sessions[index - 1]?.sessionKey);
                const showStartSessionButton = isNextInSequence && !isCompleted && !isActive && isHost();
                
                // Show timer for players when game is published/started OR timer has started
                // This ensures players see the timer card even before the host starts it, and when timer is running
                const isFirstSessionReady = index === 0 && (localGameStatus === 'published' || localGameStatus === 'started' || game?.timer_started_at);
                const showTimerForPlayers = isFirstSessionReady && !isHost();
                
                // Show timer for hosts even in draft mode so they can see the duration
                const showTimerForHostsInDraft = isHost() && localGameStatus === 'draft' && !isActive && !isCompleted;
                
                return (
                  <div
                    key={session.sessionKey}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: isActive ? '2px solid #D702F6' : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      padding: '2rem',
                      textAlign: 'center',
                      position: 'relative',
                      filter: isLocked ? 'blur(8px)' : 'none',
                      opacity: isLocked ? 0.5 : 1,
                      pointerEvents: isLocked ? 'none' : 'auto',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <h2 style={{ color: '#fff', marginBottom: '1rem', fontSize: '1.5rem' }}>
                      {session.name}
                    </h2>
                    
                    {/* Start Game Button - Only in first card when game hasn't started - Host only */}
                    {showStartGameButton && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleStartGame(e);
                        }}
                        style={{
                          padding: '0.75rem 2rem',
                          background: '#D702F6',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '1rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          marginBottom: '1.5rem',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 4px 15px rgba(215, 2, 246, 0.4)'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.background = '#C001E5';
                          e.target.style.boxShadow = '0 6px 20px rgba(215, 2, 246, 0.6)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = '#D702F6';
                          e.target.style.boxShadow = '0 4px 15px rgba(215, 2, 246, 0.4)';
                        }}
                      >
                        Start Game
                      </button>
                    )}
                    
                    {/* Start Session Button - For sessions ready to start - Host only */}
                    {showStartSessionButton && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleStartSession(index);
                        }}
                        style={{
                          padding: '0.75rem 2rem',
                          background: '#D702F6',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '1rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          marginBottom: '1.5rem',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 4px 15px rgba(215, 2, 246, 0.4)'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.background = '#C001E5';
                          e.target.style.boxShadow = '0 6px 20px rgba(215, 2, 246, 0.6)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = '#D702F6';
                          e.target.style.boxShadow = '0 4px 15px rgba(215, 2, 246, 0.4)';
                        }}
                      >
                        Start Session
                      </button>
                    )}
                    
                    {/* Session Complete Message */}
                    {isCompleted && (
                      <div style={{
                        background: 'rgba(76, 175, 80, 0.2)',
                        border: '2px solid rgba(76, 175, 80, 0.5)',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem'
                      }}>
                        <h3 style={{ color: '#ffffff', margin: 0, fontSize: '1.25rem' }}>
                          🎉 Session Complete!
                        </h3>
                      </div>
                    )}
                    
                    {/* Locked Message */}
                    {isLocked && (
                      <div style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '1rem',
                        marginBottom: '1rem',
                        fontWeight: 500
                      }}>
                        Session {index + 1} - Locked
                      </div>
                    )}
                    
                    {/* Timer Display - Show for active, completed, ready-to-start sessions, for players when game is started, or for hosts in draft mode */}
                    {(isActive || isCompleted || showStartSessionButton || showStartGameButton || showTimerForPlayers || showTimerForHostsInDraft) && (
                      <>
                        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                          <NeonCountdown
                            size="lg"
                            seconds={
                              isCompleted
                                ? 0
                                : isActive && timeRemaining != null
                                  ? timeRemaining
                                  : (session?.timerDuration || 15) * 60
                            }
                            tone={
                              isCompleted
                                ? 'complete'
                                : (isActive && timeRemaining != null && timeRemaining < 60 ? 'urgent' : 'default')
                            }
                          />
                        </div>
                        
                        {/* Voice Waveform - Only for active session */}
                        {isActive && isConfigured() && currentAudioUrl && (
                          <div style={{ marginTop: '1.5rem' }}>
                            <div style={{ 
                              color: 'rgba(255, 255, 255, 0.7)', 
                              fontSize: '0.875rem', 
                              marginBottom: '0.5rem' 
                            }}>
                              {isVoicePlaying ? 'AI Voice Reading...' : 'Voice Ready'}
                            </div>
                            <VoiceWaveform
                              audioUrl={currentAudioUrl}
                              isPlaying={isVoicePlaying}
                              onFinish={() => {
                                console.log('[VOICE-FINISH] ✅ VoiceWaveform finished - cleaning up');
                                console.log('[VOICE-FINISH] Current audio URL:', currentAudioUrl);
                                
                                // Clear the timeout since audio finished normally
                                if (voiceTimeoutRef.current) {
                                  clearTimeout(voiceTimeoutRef.current);
                                  voiceTimeoutRef.current = null;
                                  console.log('[VOICE-FINISH] ✅ Cleared voice timeout');
                                }
                                
                                console.log('[VOICE-FINISH] ✅ Clearing isPlayingAudioRef and state');
                                isPlayingAudioRef.current = false;
                                setIsVoicePlaying(false);
                                const urlToRevoke = currentAudioUrl;
                                setCurrentAudioUrl(null);
                                // Only revoke blob URLs, not Supabase Storage URLs
                                if (urlToRevoke && urlToRevoke.startsWith('blob:')) {
                                  console.log('[VOICE-FINISH] ✅ Revoking audio URL blob');
                                  URL.revokeObjectURL(urlToRevoke);
                                } else {
                                  console.log('[VOICE-FINISH] ℹ️ Not revoking Supabase Storage URL');
                                }
                                console.log('[VOICE-FINISH] ✅ Voice cleanup complete');
                              }}
                              onError={() => {
                                console.error('[VOICE-ERROR] ❌ VoiceWaveform error - cleaning up');
                                
                                // Clear the timeout on error
                                if (voiceTimeoutRef.current) {
                                  clearTimeout(voiceTimeoutRef.current);
                                  voiceTimeoutRef.current = null;
                                }
                                
                                isPlayingAudioRef.current = false;
                                setIsVoicePlaying(false);
                                const urlToRevoke = currentAudioUrl;
                                setCurrentAudioUrl(null);
                                if (urlToRevoke && urlToRevoke.startsWith('blob:')) {
                                  URL.revokeObjectURL(urlToRevoke);
                                }
                              }}
                              height={60}
                              waveColor="#FFFFFF"
                              progressColor="#D702F6"
                            />
                          </div>
                        )}
                        
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Resources Section */}
          {/* Show for hosts always, or for players when game is published/started OR timer has started */}
          {(isHost() || game?.game_status === 'published' || game?.game_status === 'started' || game?.timer_started_at) && (
            <div style={{ marginBottom: '2rem' }}>
              <ResourceSection
                resources={resources}
                setResources={setResources}
                onResourcesChange={loadResources}
                currentGameId={game?.id}
              />
            </div>
          )}

          {/* Waiting Message for Players - Show when game is published but timer hasn't started */}
          {(() => {
            // Show waiting screen ONLY if:
            // - Not host
            // - Game status is 'draft' or 'published' (explicit check for stability)
            // - Timer has NOT started (timer_started_at is null) - this is the source of truth
            // NOTE: Removed timerStarted from condition - it's updated by polling/subscriptions
            // and causes flickering. Rely only on game?.timer_started_at from database.
            // Use stableWaitingScreen (debounced) instead of calculating here to prevent flickering
            return stableWaitingScreen && (
              <div style={{
                background: 'rgba(215, 2, 246, 0.1)',
                border: '2px solid rgba(215, 2, 246, 0.3)',
                borderRadius: '12px',
                padding: '3rem',
                marginBottom: '2rem',
                textAlign: 'center'
              }}>
                <h3 style={{ color: '#ffffff', marginBottom: '1rem', fontSize: '1.5rem', fontWeight: 600 }}>
                  Thank you for joining!
                </h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '1.1rem', lineHeight: '1.6', margin: 0 }}>
                  Please wait for the host to start the game. Once the game starts you will find important details here.
                </p>
              </div>
            );
          })()}

          {/* Session Info - Only show active session */}
          {/* Show for hosts always, or for players when game is published/started OR timer has started */}
          {(() => {
            // For hosts, use currentSession or first session if available
            // For players, only show if timer has started
            const sessionToShow = isHost() 
              ? (currentSession || (sessions.length > 0 ? sessions[0] : null))
              : currentSession;
            
            // Debug logging for Session Details visibility
            if (isHost()) {
              console.log('[SESSION-DETAILS] Visibility check:', {
                isHost: true,
                currentSession: !!currentSession,
                currentSessionIndex,
                sessionsLength: sessions.length,
                sessionToShow: !!sessionToShow,
                localGameStatus,
                timerStarted: game?.timer_started_at,
                sessions: sessions.map(s => ({ key: s.sessionKey, name: s.name }))
              });
            }
            
            // For hosts, show Session Details if game is published/started, even if sessionToShow is null (sessions might still be loading)
            // For players, require sessionToShow AND game to be published/started/timer started
            const shouldShow = isHost() 
              ? (localGameStatus === 'published' || localGameStatus === 'started' || game?.timer_started_at || game?.game_status === 'published' || game?.game_status === 'started')
              : (sessionToShow && (localGameStatus === 'published' || localGameStatus === 'started' || game?.timer_started_at));
            
            if (!shouldShow) {
              if (isHost()) {
                console.log('[SESSION-DETAILS] Not showing because:', {
                  isHost: true,
                  localGameStatus,
                  gameStatus: game?.game_status,
                  timerStarted: game?.timer_started_at,
                  sessionToShow: !!sessionToShow,
                  shouldShow: false
                });
              }
              return null;
            }
            
            // If sessionToShow is null but we should show (host case), use first session or show loading state
            const effectiveSessionToShow = sessionToShow || (isHost() && sessions.length > 0 ? sessions[0] : null);
            
            if (!effectiveSessionToShow && isHost()) {
              // Sessions are still loading - show a loading state or return null
              console.log('[SESSION-DETAILS] Sessions still loading, showing loading state');
              return (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '2rem'
                }}>
                  <h3 style={{ color: '#ffffff', marginBottom: '1.5rem', fontSize: '1.25rem' }}>
                    Session Details
                  </h3>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontStyle: 'italic' }}>
                    Loading session information...
                  </p>
                </div>
              );
            }
            
            if (!effectiveSessionToShow) {
              return null;
            }
            
            return (
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '2rem',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden'
              }}>
                <h3 style={{ color: '#ffffff', marginBottom: '1.5rem', fontSize: '1.25rem' }}>
                  Session Details
                </h3>
              
              {/* Session Name */}
              <div style={{ marginBottom: '1.5rem' }}>
                <strong style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.95rem', display: 'block', marginBottom: '0.5rem' }}>
                  Session Name:
                </strong>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6', fontSize: '1rem' }}>
                  {effectiveSessionToShow.name || effectiveSessionToShow.session_display_name || 'Not set'}
                </p>
              </div>

              {/* Creative Brief */}
              <div style={{ marginBottom: '1.5rem' }}>
                <strong style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.95rem', display: 'block', marginBottom: '0.5rem' }}>
                  Creative Brief:
                </strong>
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  lineHeight: '1.6',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}>
                  {effectiveSessionToShow.creativeBrief || effectiveSessionToShow.brief_text || effectiveSessionToShow.creative_brief || 'Not set'}
                </p>
              </div>

              {/* Target Audience */}
              <div style={{ marginBottom: '1.5rem' }}>
                <strong style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.95rem', display: 'block', marginBottom: '0.5rem' }}>
                  Target Audience:
                </strong>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.6' }}>
                  {effectiveSessionToShow.targetAudience || effectiveSessionToShow.target_audience || 'Not set'}
                </p>
              </div>

              {/* Requirements */}
              <div style={{ marginBottom: '1.5rem' }}>
                <strong style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.95rem', display: 'block', marginBottom: '0.5rem' }}>
                  Requirements:
                </strong>
                {sessionRequirements.length > 0 ? (
                  <ul style={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    lineHeight: '1.8',
                    paddingLeft: '1.5rem',
                    margin: 0
                  }}>
                    {sessionRequirements.map((req, index) => (
                      <li key={index} style={{ marginBottom: '0.5rem' }}>
                        {req}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
                    No requirements set
                  </p>
                )}
              </div>

              {/* Client Changes Section - Show to hosts always, but Read Aloud buttons only when timer is running */}
              {isHost() && (() => {
                // Use effectiveSessionToShow from parent scope, or fallback to currentSession or first session
                const sessionForChanges = effectiveSessionToShow || currentSession || (sessions.length > 0 ? sessions[0] : null);
                if (!sessionForChanges) return null;
                
                return (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <strong style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.95rem', display: 'block', marginBottom: '0.75rem' }}>
                      Client Changes:
                    </strong>
                    {clientChanges.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {clientChanges.map((change, index) => {
                          const isGenerating = generatingVoiceForIndex === index;
                          const changeText = change.text || change.change_text || '';
                          const sessionName = sessionForChanges?.session_name || sessionForChanges?.sessionKey;
                        
                        // Debug logging for button visibility
                        const buttonShouldShow = isHost() && isConfigured() && sessionName && timerStarted;
                        if (index === 0) {
                          console.log('[CLIENT-CHANGES] Button visibility check:', {
                            isHost: isHost(),
                            isConfigured: isConfigured(),
                            sessionName,
                            timerStarted,
                            buttonShouldShow,
                            currentSessionIndex,
                            sessionsLength: sessions.length
                          });
                        }
                        
                        const hasBeenRead = readChanges.has(`change_${index}`);
                        return (
                          <div
                            key={index}
                            style={{
                              padding: '1rem',
                              background: hasBeenRead 
                                ? 'rgba(34, 197, 94, 0.15)' 
                                : 'rgba(255, 255, 255, 0.05)',
                              border: hasBeenRead 
                                ? '1px solid rgba(34, 197, 94, 0.5)' 
                                : '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '8px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '1rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <p style={{ 
                              color: 'rgba(255, 255, 255, 0.8)', 
                              margin: 0, 
                              flex: 1,
                              lineHeight: '1.6'
                            }}>
                              {changeText}
                            </p>
                            {isHost() && isConfigured() && sessionName && timerStarted && (
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (hasBeenRead) return; // Prevent re-reading
                                  
                                  console.log(`[VOICE-BUTTON] 🎤 Read Aloud clicked for change ${index}`, {
                                    changeIndex: index,
                                    changeText: changeText.substring(0, 50),
                                    sessionName,
                                    gameId: game?.id,
                                    timerStarted,
                                    isConfigured: isConfigured()
                                  });
                                  setGeneratingVoiceForIndex(index);
                                  
                                  // Safety timeout - if generation takes more than 45 seconds, reset the UI
                                  const safetyTimeout = setTimeout(() => {
                                    console.error(`[VOICE-BUTTON] ⏱️ Safety timeout triggered - generation took too long`);
                                    setGeneratingVoiceForIndex(null);
                                    alert('Voice generation timed out. Please try again.');
                                  }, 45000);
                                  
                                  try {
                                    await generateAndStoreVoiceClip(change, index, sessionName);
                                    clearTimeout(safetyTimeout);
                                    console.log(`[VOICE-BUTTON] ✅ Successfully generated voice for change ${index}`);
                                    // Mark this change as read
                                    setReadChanges(prev => new Set([...prev, `change_${index}`]));
                                  } catch (error) {
                                    clearTimeout(safetyTimeout);
                                    console.error(`[VOICE-BUTTON] ❌ Error generating voice for change ${index}:`, error);
                                    console.error(`[VOICE-BUTTON] Error details:`, {
                                      message: error.message,
                                      stack: error.stack,
                                      changeIndex: index,
                                      sessionName
                                    });
                                    
                                    // Check if it's a quota error or timeout
                                    const errorMessage = error.message || '';
                                    if (errorMessage.includes('quota_exceeded') || errorMessage.includes('quota')) {
                                      alert('ElevenLabs API quota exceeded. Please add more credits to your ElevenLabs account to generate voice.');
                                    } else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
                                      alert('ElevenLabs API authentication failed. Please check your API key configuration.');
                                    } else if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
                                      alert('Voice generation timed out. The ElevenLabs API may be slow. Please try again.');
                                    } else {
                                      alert(`Failed to generate voice: ${error.message || 'Unknown error'}. Check console for details.`);
                                    }
                                  } finally {
                                    setGeneratingVoiceForIndex(null);
                                  }
                                }}
                                disabled={hasBeenRead || isGenerating || isVoicePlaying}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: hasBeenRead
                                    ? 'rgba(34, 197, 94, 0.3)'
                                    : isGenerating 
                                    ? 'rgba(215, 2, 246, 0.5)' 
                                    : '#D702F6',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  cursor: (hasBeenRead || isGenerating || isVoicePlaying) ? 'not-allowed' : 'pointer',
                                  transition: 'all 0.2s ease',
                                  opacity: (hasBeenRead || isGenerating || isVoicePlaying) ? 0.7 : 1,
                                  whiteSpace: 'nowrap',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}
                                onMouseOver={(e) => {
                                  if (!hasBeenRead && !isGenerating && !isVoicePlaying) {
                                    e.target.style.background = '#C001E5';
                                  }
                                }}
                                onMouseOut={(e) => {
                                  if (!hasBeenRead && !isGenerating && !isVoicePlaying) {
                                    e.target.style.background = '#D702F6';
                                  }
                                }}
                              >
                                {hasBeenRead ? (
                                  <>
                                    <span style={{ fontSize: '1rem' }}>✓</span>
                                    <span>Read</span>
                                  </>
                                ) : isGenerating ? (
                                  '⏳ Generating...'
                                ) : (
                                  '🎤 Read Aloud'
                                )}
                              </button>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Bonus Change */}
                      {bonusChange && (() => {
                        const bonusHasBeenRead = readChanges.has('bonus');
                        return (
                        <div
                          style={{
                            padding: '1rem',
                              background: bonusHasBeenRead
                                ? 'rgba(34, 197, 94, 0.15)'
                                : 'rgba(255, 193, 7, 0.1)',
                              border: bonusHasBeenRead
                                ? '1px solid rgba(34, 197, 94, 0.5)'
                                : '1px solid rgba(255, 193, 7, 0.3)',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                              gap: '1rem',
                              transition: 'all 0.3s ease'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                                color: bonusHasBeenRead
                                  ? 'rgba(34, 197, 94, 0.9)'
                                  : 'rgba(255, 193, 7, 0.9)', 
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              marginBottom: '0.25rem'
                            }}>
                              BONUS
                            </div>
                            <p style={{ 
                              color: 'rgba(255, 255, 255, 0.8)', 
                              margin: 0, 
                              lineHeight: '1.6'
                            }}>
                              {bonusChange.text || bonusChange.change_text || ''}
                            </p>
                          </div>
                          {isHost() && isConfigured() && timerStarted && (() => {
                            // Use effectiveSessionToShow from parent scope, or fallback to currentSession
                            const sessionForBonus = effectiveSessionToShow || sessions[currentSessionIndex];
                            const sessionName = sessionForBonus?.session_name || sessionForBonus?.sessionKey;
                            return sessionName ? (
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (bonusHasBeenRead) return; // Prevent re-reading
                                    
                                  setGeneratingVoiceForIndex(-1);
                                  
                                  // Safety timeout - if generation takes more than 45 seconds, reset the UI
                                  const safetyTimeout = setTimeout(() => {
                                    console.error(`[VOICE-BUTTON] ⏱️ Safety timeout triggered for bonus - generation took too long`);
                                    setGeneratingVoiceForIndex(null);
                                    alert('Voice generation timed out. Please try again.');
                                  }, 45000);
                                  
                                  try {
                                    await generateAndStoreVoiceClip(bonusChange, -1, sessionName);
                                    clearTimeout(safetyTimeout);
                                    // Mark bonus as read
                                    setReadChanges(prev => new Set([...prev, 'bonus']));
                                  } catch (error) {
                                    clearTimeout(safetyTimeout);
                                    console.error(`[VOICE-BUTTON] ❌ Error generating bonus voice:`, error);
                                      
                                    // Check if it's a quota error or timeout
                                    const errorMessage = error.message || '';
                                    if (errorMessage.includes('quota_exceeded') || errorMessage.includes('quota')) {
                                      alert('ElevenLabs API quota exceeded. Please add more credits to your ElevenLabs account to generate voice.');
                                    } else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
                                      alert('ElevenLabs API authentication failed. Please check your API key configuration.');
                                    } else if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
                                      alert('Voice generation timed out. The ElevenLabs API may be slow. Please try again.');
                                    } else {
                                      alert(`Failed to generate voice: ${error.message || 'Unknown error'}. Please try again.`);
                                    }
                                  } finally {
                                    setGeneratingVoiceForIndex(null);
                                  }
                                }}
                                  disabled={bonusHasBeenRead || generatingVoiceForIndex === -1 || isVoicePlaying}
                                style={{
                                  padding: '0.5rem 1rem',
                                    background: bonusHasBeenRead
                                      ? 'rgba(34, 197, 94, 0.3)'
                                      : generatingVoiceForIndex === -1
                                    ? 'rgba(255, 193, 7, 0.5)' 
                                    : 'rgba(255, 193, 7, 0.8)',
                                    color: bonusHasBeenRead ? '#ffffff' : '#000000',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                    cursor: (bonusHasBeenRead || generatingVoiceForIndex === -1 || isVoicePlaying) ? 'not-allowed' : 'pointer',
                                  transition: 'all 0.2s ease',
                                    opacity: (bonusHasBeenRead || generatingVoiceForIndex === -1 || isVoicePlaying) ? 0.7 : 1,
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}
                                onMouseOver={(e) => {
                                    if (!bonusHasBeenRead && generatingVoiceForIndex !== -1 && !isVoicePlaying) {
                                    e.target.style.background = 'rgba(255, 193, 7, 1)';
                                  }
                                }}
                                onMouseOut={(e) => {
                                    if (!bonusHasBeenRead && generatingVoiceForIndex !== -1 && !isVoicePlaying) {
                                    e.target.style.background = 'rgba(255, 193, 7, 0.8)';
                                  }
                                }}
                              >
                                  {bonusHasBeenRead ? (
                                    <>
                                      <span style={{ fontSize: '1rem' }}>✓</span>
                                      <span>Read</span>
                                    </>
                                  ) : generatingVoiceForIndex === -1 ? (
                                    '⏳ Generating...'
                                  ) : (
                                    '🎤 Read Aloud'
                                  )}
                              </button>
                            ) : null;
                          })()}
                        </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
                      No client changes for this session
                    </p>
                  )}
                  
                  {/* Voice Configuration Warning - Host Only */}
                  {isHost() && !isConfigured() && (
                    <div style={{ 
                      marginTop: '1rem', 
                      padding: '0.75rem', 
                      background: 'rgba(255, 193, 7, 0.1)', 
                      border: '1px solid rgba(255, 193, 7, 0.3)',
                      borderRadius: '6px',
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.875rem'
                    }}>
                      ⚠️ Voice reading disabled: ElevenLabs API key not configured
                    </div>
                  )}
                  </div>
                );
              })()}
              </div>
            );
          })()}

              {/* Designer Upload Section - Only show for designers, not hosts */}
              {/* Show when game is started OR timer has started */}
              {!isHost() && currentSession && (localGameStatus === 'started' || game?.timer_started_at) && (() => {
                // Find the current user's designer info
                const currentUserDesigner = designers.find(d => {
                  const designer = typeof d === 'string' ? { name: d } : d;
                  return user?.id && designer?.userId === user.id;
                });
                
                if (!currentUserDesigner) {
                  console.log('Designer upload: No matching designer found', {
                    userId: user?.id,
                    designers: designers.map(d => ({
                      name: typeof d === 'string' ? d : d.name,
                      userId: typeof d === 'string' ? null : d.userId
                    }))
                  });
                  return null;
                }
                
                const designerName = typeof currentUserDesigner === 'string' 
                  ? currentUserDesigner 
                  : currentUserDesigner.name;
                const hasUploaded = submissions.some(sub => sub.designerName === designerName);
                const isTimerFinished = game?.game_status === 'finished';
                const canUpload = timerStarted && !isTimerFinished;
                
                return (
                  <>
                    {/* Horizontal Divider */}
                    <div style={{
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                      marginTop: '2rem',
                      marginBottom: '2rem'
                    }} />
                    
                    {/* Designer Upload Area */}
                    <div style={{ marginBottom: '2rem' }}>
                      <h3 style={{ color: '#ffffff', marginBottom: '1rem', fontSize: '1.25rem' }}>
                        Upload Your Design
                      </h3>
                      
                      {hasUploaded ? (
                        <div>
                          {/* Success Message */}
                        <div style={{
                          background: 'rgba(76, 175, 80, 0.1)',
                          border: '2px solid rgba(76, 175, 80, 0.3)',
                          borderRadius: '8px',
                            padding: '1rem',
                            textAlign: 'center',
                            marginBottom: '1rem'
                        }}>
                            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>✓</div>
                            <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, marginBottom: '0.25rem', fontSize: '0.95rem' }}>
                            Design uploaded successfully!
                          </p>
                          <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem', margin: 0 }}>
                              Upload a new design below to replace it.
                            </p>
                          </div>
                          
                          {/* Replace Upload Area */}
                          <div
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (canUpload && !isUploadingDesignerSubmission) {
                                setIsDragging(true);
                              }
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsDragging(false);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsDragging(false);
                              
                              if (!canUpload || isUploadingDesignerSubmission) return;
                              
                              const files = Array.from(e.dataTransfer.files);
                              const imageFile = files.find(file => file.type.startsWith('image/'));
                              
                              if (imageFile) {
                                handleDesignerUpload(imageFile, designerName);
                              } else {
                                alert('Please drop an image file.');
                              }
                            }}
                            onClick={() => {
                              if (canUpload && !isUploadingDesignerSubmission && designerFileInputRef.current) {
                                designerFileInputRef.current.click();
                              }
                            }}
                            style={{
                              border: `2px dashed ${isDragging ? '#D702F6' : 'rgba(255, 255, 255, 0.2)'}`,
                              borderRadius: '12px',
                              padding: '2rem 1.5rem',
                              textAlign: 'center',
                              cursor: canUpload && !isUploadingDesignerSubmission ? 'pointer' : 'not-allowed',
                              background: isDragging 
                                ? 'rgba(215, 2, 246, 0.1)' 
                                : 'rgba(255, 255, 255, 0.03)',
                              transition: 'all 0.2s ease',
                              opacity: canUpload ? 1 : 0.5
                            }}
                            onMouseEnter={(e) => {
                              if (canUpload && !isUploadingDesignerSubmission) {
                                e.currentTarget.style.borderColor = '#D702F6';
                                e.currentTarget.style.background = 'rgba(215, 2, 246, 0.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isDragging) {
                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                              }
                            }}
                          >
                            {isUploadingDesignerSubmission ? (
                              <>
                                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
                                <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0 }}>
                                  Uploading replacement...
                                </p>
                              </>
                            ) : (
                              <>
                                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🔄</div>
                                <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, marginBottom: '0.5rem', fontSize: '1rem', fontWeight: 500 }}>
                                  Replace Your Design
                                </p>
                                <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0, fontSize: '0.875rem' }}>
                                  Drag and drop a new image here or click to select
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (canUpload && !isUploadingDesignerSubmission) {
                              setIsDragging(true);
                            }
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragging(false);
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragging(false);
                            
                            if (!canUpload || isUploadingDesignerSubmission) return;
                            
                            const files = Array.from(e.dataTransfer.files);
                            const imageFile = files.find(file => file.type.startsWith('image/'));
                            
                            if (imageFile) {
                              handleDesignerUpload(imageFile, designerName);
                            } else {
                              alert('Please drop an image file.');
                            }
                          }}
                          onClick={() => {
                            if (canUpload && !isUploadingDesignerSubmission && designerFileInputRef.current) {
                              designerFileInputRef.current.click();
                            }
                          }}
                          style={{
                            border: `2px dashed ${isDragging ? '#D702F6' : 'rgba(255, 255, 255, 0.2)'}`,
                            borderRadius: '12px',
                            padding: '3rem 2rem',
                            textAlign: 'center',
                            cursor: canUpload && !isUploadingDesignerSubmission ? 'pointer' : 'not-allowed',
                            background: isDragging 
                              ? 'rgba(215, 2, 246, 0.1)' 
                              : 'rgba(255, 255, 255, 0.03)',
                            transition: 'all 0.2s ease',
                            opacity: canUpload ? 1 : 0.5
                          }}
                          onMouseEnter={(e) => {
                            if (canUpload && !isUploadingDesignerSubmission) {
                              e.currentTarget.style.borderColor = '#D702F6';
                              e.currentTarget.style.background = 'rgba(215, 2, 246, 0.05)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isDragging) {
                              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                            }
                          }}
                        >
                          {isUploadingDesignerSubmission ? (
                            <>
                              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
                              <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0 }}>
                                Uploading...
                              </p>
                            </>
                          ) : !timerStarted ? (
                            <>
                              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⏸️</div>
                              <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, marginBottom: '0.5rem', fontSize: '1rem', fontWeight: 500 }}>
                                Waiting for session to start
                              </p>
                              <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0, fontSize: '0.875rem' }}>
                                Upload will be available once the timer starts
                              </p>
                            </>
                          ) : (
                            <>
                              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📤</div>
                              <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, marginBottom: '0.5rem', fontSize: '1rem', fontWeight: 500 }}>
                                Drag and drop your design here
                              </p>
                              <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0, fontSize: '0.875rem' }}>
                                or click to select a file
                              </p>
                            </>
                          )}
                        </div>
                      )}
                      
                      <input
                        ref={designerFileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file && canUpload && !isUploadingDesignerSubmission) {
                            handleDesignerUpload(file, designerName);
                          }
                          e.target.value = '';
                        }}
                      />
                    </div>
                  </>
                );
              })()}

              {/* Horizontal Divider */}
              {timerStarted && (
                <div style={{
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  marginTop: '2rem',
                  marginBottom: '2rem'
                }} />
              )}

              {/* Design Upload Status Section - Only show for hosts */}
              {isHost() && timerStarted && (
                <div>
                  <h3 style={{ color: '#ffffff', marginBottom: '1.5rem', fontSize: '1.25rem' }}>
                    Design Upload Status
                  </h3>

                  {/* Debug info - will be logged to console */}
                  {(() => {
                    console.log('[UPLOAD-STATUS] 🔍 Render check:', {
                      isHost: isHost(),
                      timerStarted,
                      designersCount: designers.length,
                      submissionsCount: submissions.length,
                      currentSessionIndex,
                      currentSessionKey: sessions[currentSessionIndex]?.sessionKey,
                      designerNames: designers.map(d => d.name || d),
                      submissionNames: submissions.map(s => s.designerName)
                    });
                    return null;
                  })()}

                  {/* Designer Status List */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '1rem',
                    marginBottom: '2rem'
                  }}>
                    {designers
                      .map((designer, index) => {
                      const designerName = designer.name || designer;
                      // Normalize names for comparison (case-insensitive, trimmed)
                      const normalizedDesignerName = (designerName || '').toLowerCase().trim();
                      const hasUploaded = submissions.some(sub => {
                        const normalizedSubmissionName = (sub.designerName || '').toLowerCase().trim();
                        const matches = normalizedSubmissionName === normalizedDesignerName;
                        if (matches) {
                          console.log('[UPLOAD-STATUS] ✅ Match found:', {
                            designerName,
                            submissionDesignerName: sub.designerName,
                            normalizedDesignerName,
                            normalizedSubmissionName
                          });
                        }
                        return matches;
                      });
                      
                      // Log all submissions for debugging
                      if (index === 0 && submissions.length > 0) {
                        console.log('[UPLOAD-STATUS] 🔍 All submissions:', submissions.map(s => ({
                          designerName: s.designerName,
                          normalized: (s.designerName || '').toLowerCase().trim(),
                          sessionName: s.session_name || 'N/A',
                          id: s.id
                        })));
                      }
                      
                      // Debug logging for all designers
                      console.log(`[UPLOAD-STATUS] Designer ${index + 1}/${designers.length}:`, {
                        designerName,
                        normalizedDesignerName,
                        hasUploaded,
                        submissionsCount: submissions.length
                      });
                      
                      return (
                        <div
                          key={index}
                          style={{
                            padding: '1rem',
                            background: hasUploaded 
                              ? 'rgba(76, 175, 80, 0.1)' 
                              : 'rgba(255, 255, 255, 0.05)',
                            border: hasUploaded
                              ? '2px solid rgba(76, 175, 80, 0.5)'
                                : '2px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <div style={{
                            fontSize: '1.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: hasUploaded 
                              ? 'rgba(76, 175, 80, 0.2)' 
                              : 'rgba(255, 255, 255, 0.1)',
                            flexShrink: 0
                          }}>
                            {hasUploaded ? '✓' : '⏳'}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              color: '#ffffff', 
                              fontWeight: 500,
                              fontSize: '0.95rem',
                              marginBottom: '0.25rem'
                            }}>
                              {designerName}
                            </div>
                            <div style={{ 
                              color: hasUploaded 
                                ? 'rgba(76, 175, 80, 0.9)' 
                                : 'rgba(255, 255, 255, 0.6)',
                              fontSize: '0.875rem'
                            }}>
                              {hasUploaded ? 'Design uploaded' : 'Waiting for upload'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Leaderboard */}
                  <div style={{
                    marginTop: '2rem',
                    paddingTop: '2rem',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    {(() => {
                      const currentSessionSubmissions = submissions.filter(sub => {
                        // Match submissions to current session
                        // Submissions are already filtered by session_name in loadSubmissions
                        return true; // All submissions in state are for current session
                      });
                      
                      console.log('[LEADERBOARD] Current session:', currentSession?.sessionKey);
                      console.log('[LEADERBOARD] All submissions:', submissions);
                      console.log('[LEADERBOARD] Filtered submissions:', currentSessionSubmissions);
                      
                      return (
                    <Leaderboard 
                          submissions={currentSessionSubmissions.map(sub => ({
                        id: sub.id,
                        designerName: sub.designerName,
                        scores: sub.scores || {},
                        imageUrl: sub.imageUrl
                      }))}
                      requirements={clientChanges.filter(change => change.sessionName === currentSession?.sessionKey)}
                    />
                      );
                    })()}
                  </div>
                </div>
              )}

          {/* Game Complete Message - Only show when all sessions timer has run out */}
          {allSessionsComplete && (
            <div style={{
              background: 'rgba(76, 175, 80, 0.2)',
              border: '2px solid rgba(76, 175, 80, 0.5)',
              borderRadius: '12px',
              padding: '2rem',
              marginTop: '2rem',
              textAlign: 'center'
            }}>
              <h3 style={{ color: '#ffffff', marginBottom: '1rem', fontSize: '1.5rem' }}>
                🎉 Game Complete!
              </h3>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1.5rem' }}>
                {isHost() 
                  ? "All sessions have finished. Ready to review and score submissions?"
                  : "All sessions have finished. View all submissions and scores?"}
              </p>
              {onNavigateToReview && (
                <button
                  onClick={onNavigateToReview}
                  style={{
                    padding: '0.75rem 2rem',
                    background: '#D702F6',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#C001E5';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#D702F6';
                  }}
                >
                  {isHost() ? 'Review & Score Submissions' : 'View Submissions'}
                </button>
              )}
            </div>
          )}
        </div>
      </main>
    </>
  );
}

export default ActiveGameView;

